import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { allocateGlobalTip, isQuickChargeOrderCode, KIOSK_DINE_IN_CODE_PREFIX, QUICK_CHARGE_CODE_PREFIX, resolveTip } from 'shared-models/browser';
import { toHebrewApiErrorMessage } from '../../../services/index/index.service';
import { OrderView, ordersService, OrderAlreadyClosedError } from '../../../services/orders/orders.service';
import { ProductView, productsService } from '../../../services/products/products.service';
import { CategoryView, categoriesService } from '../../../services/categories/categories.service';
import { OrderlineView, orderlinesService } from '../../../services/orderlines/orderlines.service';
import { PaymentlineView, paymentsService, CashDrawerSelectionRequiredError } from '../../../services/payments/payments.service';
import type { SplitPaymentlinePayload, TipPayload, CashDrawerOption, SplitAndPayRequest } from '../../../services/payments/payments.service';
import { creditPaymentsService } from '../../../services/credit-payments/credit-payments.service';
import type { PaymentTerminalView } from '../../../services/credit-payments/credit-payments.service';
import { tapOnPhoneBridge } from '../../../services/tap-on-phone/tap-on-phone-bridge';
import { logToBackend } from '../../../services/client-log/client-log.service';
import type { ChargeManualCreditRequest } from 'shared-models/browser';
import { SessionView } from '../../../services/sessions/sessions.service';
import { mergeService, MergeLink } from '../../../services/merge/merge.service';
import { transferService } from '../../../services/transfer/transfer.service';
import type { TransferOrderlineMove } from '../../../services/transfer/transfer.service';
import type { PosStreamEvent } from '../../../services/pos-stream/pos-stream.service';
import { mergeById, removeById } from '../core/pos-shell-page.helpers';
import { calculateOrderDiscount } from '../components/orders/shared/discount.utils';
import { filterProductsBySearch } from '../components/orders/menu/category-menu.utils';
import { printersService } from '../../../services/printers/printers.service';
import { resolveBillableDrawerId } from './fixed-printer.util';

type CreditTransactionResolvedEvent = Extract<PosStreamEvent, { type: 'credit.transaction.resolved' }>;
type CreditTransactionPendingEvent = Extract<PosStreamEvent, { type: 'credit.transaction.pending' }>;
type TapOnPhonePendingEvent = Extract<PosStreamEvent, { type: 'credit.tap.pending' }>;
type TapOnPhoneReleasedEvent = Extract<PosStreamEvent, { type: 'credit.tap.released' }>;
type PaymentOveragePrompt = { orderId: number; paymentlineId?: number; overpaidAmount: number };
// Multi-drawer cash close needs a drawer chosen once per order (Rule 41); carries the retry context.
// 'close' covers settled/zero-total closes finalizing an order paid by intermediate cash tenders.
type CashDrawerPrompt =
  | { mode: 'pay' | 'overpay'; orderId: number; paymentlineId: number; drawers: CashDrawerOption[] }
  | {
      mode: 'settle-overage';
      orderId: number;
      paymentlineId: number;
      tenderedAmount: number;
      action: 'return_change' | 'add_to_tip';
      drawers: CashDrawerOption[];
    }
  | { mode: 'close'; orderId: number; drawers: CashDrawerOption[] };
type PaymentClosePrompt = {
  orderId: number;
  reason: 'zero_total' | 'settled';
};

export type TransferMode =
  | { step: 'idle' }
  | { step: 'item-select'; sourceOrderId: number; sourceTableId: number | null; sourceTableIds: number[]; sourceTableLabel: string }
  | { step: 'pick-table'; sourceOrderId: number; sourceTableId: number | null; sourceTableIds: number[]; sourceTableLabel: string; selectedLines: TransferOrderlineMove[] };

export type MergeMode =
  | { step: 'idle' }
  | { step: 'pick-tables'; centralTableId: number; centralTableLabel: string; centralOrderId: number | null; selectedTableIds: number[] };

type UsePosOrdersDomainParams = {
  tenantId: string;
  employeeSession: string | null;
  activeSession: SessionView | null;
  stationaryName?: string | null;
  setError: (message: string | null) => void;
  onAfterFullPayment: () => void;
  onNeedDinersForMerge: (centralTableId: number, centralTableLabel: string) => void;
  onReceiptAutoPrint?: (orderId: number, options?: { paymentlineId?: number; cashDrawerPrinterId?: number }) => void;
  onTakeAwayKitchenPrint?: (order: OrderView, lines: OrderlineView[], posProducts: ProductView[]) => void;
};

const MAX_ORDERLINE_QUANTITY = 99;
// Lookback hours for the אמת recovery; server anchors the window so device TZ can't shift it.
const CREDIT_RECOVERY_LOOKBACK_HOURS = 6;
const roundMoney = (value: number): number => Math.round(value * 10) / 10;

// Summary: Owns POS orders-domain state and actions used by PosShellPage and its SSE handler.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
export function usePosOrdersDomain({
  tenantId,
  employeeSession,
  activeSession,
  stationaryName = null,
  setError,
  onAfterFullPayment,
  onNeedDinersForMerge,
  onReceiptAutoPrint,
  onTakeAwayKitchenPrint,
}: UsePosOrdersDomainParams) {
  const [activeOrders, setActiveOrders] = useState<OrderView[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isOpeningOrder, setIsOpeningOrder] = useState(false);
  const [isUpdatingDiners, setIsUpdatingDiners] = useState(false);
  const [isClosingOrder, setIsClosingOrder] = useState(false);
  const [selectedOrderTableId, setSelectedOrderTableId] = useState<number | null>(null);
  const [selectedOrderTableLabel, setSelectedOrderTableLabel] = useState<string | null>(null);
  // Explicit order-id selection (take-away/list pick); takes precedence over table selection.
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [paymentScreenOrderId, setPaymentScreenOrderId] = useState<number | null>(null);
  // Set to a QC order id when it's landed on payment while empty → the amount modal (re)opens.
  const [quickChargeAmountOrderId, setQuickChargeAmountOrderId] = useState<number | null>(null);
  const lastQuickChargeLandedRef = useRef<number | null>(null);
  const [isReceiptScreenOpen, setIsReceiptScreenOpen] = useState(false);
  const [isSavingGuestName, setIsSavingGuestName] = useState(false);
  const [paymentlines, setPaymentlines] = useState<PaymentlineView[]>([]);
  const [paymentOveragePrompt, setPaymentOveragePrompt] = useState<PaymentOveragePrompt | null>(null);
  const [cashDrawerPrompt, setCashDrawerPrompt] = useState<CashDrawerPrompt | null>(null);
  const [paymentZeroTotalPrompt, setPaymentZeroTotalPrompt] = useState<PaymentClosePrompt | null>(null);
  const [posTerminals, setPosTerminals] = useState<PaymentTerminalView[]>([]);
  const [isLoadingPaymentlines] = useState(false);
  const [isPreparingPaymentline, setIsPreparingPaymentline] = useState(false);
  const [isMarkingPaymentDone, setIsMarkingPaymentDone] = useState(false);
  const [isSplittingPayment, setIsSplittingPayment] = useState(false);
  const [isSettingTip, setIsSettingTip] = useState(false);
  const [isResolvingPaymentOverage, setIsResolvingPaymentOverage] = useState(false);
  const [isResolvingZeroTotalClose, setIsResolvingZeroTotalClose] = useState(false);
  const [lockedCreditPaymentlineIds, setLockedCreditPaymentlineIds] = useState<ReadonlySet<number>>(new Set());
  const [failedCreditPaymentlineIds, setFailedCreditPaymentlineIds] = useState<ReadonlySet<number>>(new Set());
  const [orderlines, setOrderlines] = useState<OrderlineView[]>([]);
  const [isLoadingOrderlines, setIsLoadingOrderlines] = useState(false);
  // Render-safe "lines loaded" marker: the ref flips before the fetch resolves, so this state
  // tells the UI the cart content is real (kills the שחרר שולחן first-paint flash).
  const [orderlinesLoadedOrderId, setOrderlinesLoadedOrderId] = useState<number | null>(null);
  const [isAddingOrderline, setIsAddingOrderline] = useState(false);
  const [isAdjustingOrderline, setIsAdjustingOrderline] = useState(false);
  const [isSendingToKitchen, setIsSendingToKitchen] = useState(false);
  const [posProducts, setPosProducts] = useState<ProductView[]>([]);
  const [isLoadingPosProducts, setIsLoadingPosProducts] = useState(false);
  const [posCategories, setPosCategories] = useState<CategoryView[]>([]);
  const [productSearch, setProductSearch] = useState('');
  // Frozen snapshot of the search-result product ids at the moment a product was added, plus a
  // version counter the menu uses to auto-select the "תוצאות אחרונות" temp category once per add.
  const [recentResultIds, setRecentResultIds] = useState<number[]>([]);
  const [recentResultsVersion, setRecentResultsVersion] = useState(0);
  const [transferMode, setTransferMode] = useState<TransferMode>({ step: 'idle' });
  const [isTransferring, setIsTransferring] = useState(false);
  const [mergeMode, setMergeMode] = useState<MergeMode>({ step: 'idle' });
  const [activeMergeLinks, setActiveMergeLinks] = useState<MergeLink[]>([]);
  const [splitMergeOrderPk, setSplitMergeOrderPk] = useState<number | null>(null);
  const [isSplittingMerge, setIsSplittingMerge] = useState(false);
  const splitMutationInFlightRef = useRef(false);
  const [mergeConflict, setMergeConflict] = useState<{ hasPercentDiscount: boolean; hasPercentTip: boolean } | null>(null);

  const initialOrdersLoadedForSessionRef = useRef<number | null>(null);
  const posProductsLoadedForSessionRef = useRef<string | null>(null);
  const posCategoriesLoadedForSessionRef = useRef<string | null>(null);
  const activeOrdersRef = useRef<OrderView[]>([]);
  const selectedOrderTableIdRef = useRef<number | null>(null);
  const selectedOrderIdRef = useRef<number | null>(null);
  const isPaymentScreenOpenRef = useRef(false);
  const saveGuestNameInFlightRef = useRef(false);
  const updateDinersInFlightRef = useRef(false);
  const updateDepositInFlightRef = useRef(false);
  const toggleVipInFlightRef = useRef(false);
  const loadedOrderlinesOrderIdRef = useRef<number | null>(null);
  const paymentMutationInFlightRef = useRef(false);
  const paymentRecoveryInFlightRef = useRef(false);
  const transferMutationInFlightRef = useRef(false);
  const closeOrderInFlightRef = useRef(false);
  // Retry captured when a settled close needs a drawer; replayed once the picker resolves.
  const pendingCloseRetryRef = useRef<((drawerId: number) => Promise<void>) | null>(null);

  const orderlinesRef = useRef<OrderlineView[]>([]);
  useEffect(() => {
    orderlinesRef.current = orderlines;
  }, [orderlines]);

  const paymentlinesRef = useRef<PaymentlineView[]>([]);
  useEffect(() => {
    paymentlinesRef.current = paymentlines;
  }, [paymentlines]);

  const posProductsRef = useRef<ProductView[]>([]);
  useEffect(() => {
    posProductsRef.current = posProducts;
  }, [posProducts]);

  const posCategoriesRef = useRef<CategoryView[]>([]);
  useEffect(() => {
    posCategoriesRef.current = posCategories;
  }, [posCategories]);

  const productSearchRef = useRef('');
  useEffect(() => {
    productSearchRef.current = productSearch;
  }, [productSearch]);

  // Refs let the stable SSE credit-resolved handler read live state without re-subscribing the POS stream on every paymentline change.
  const onAfterFullPaymentRef = useRef(onAfterFullPayment);
  useEffect(() => {
    onAfterFullPaymentRef.current = onAfterFullPayment;
  }, [onAfterFullPayment]);
  const onReceiptAutoPrintRef = useRef(onReceiptAutoPrint);
  useEffect(() => {
    onReceiptAutoPrintRef.current = onReceiptAutoPrint;
  }, [onReceiptAutoPrint]);
  const onTakeAwayKitchenPrintRef = useRef(onTakeAwayKitchenPrint);
  useEffect(() => {
    onTakeAwayKitchenPrintRef.current = onTakeAwayKitchenPrint;
  }, [onTakeAwayKitchenPrint]);

  const markPaymentlineAsFailed = useCallback((paymentlineId: number) => {
    setFailedCreditPaymentlineIds((previous) => {
      if (previous.has(paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(paymentlineId);
      return next;
    });
  }, []);

  const clearFailedCreditPaymentline = useCallback((paymentlineId: number) => {
    setFailedCreditPaymentlineIds((previous) => {
      if (!previous.has(paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.delete(paymentlineId);
      return next;
    });
  }, []);

  const hasOpenFailedPaymentline = useCallback((): boolean =>
    paymentlines.some((line) => line.status === 'new' && failedCreditPaymentlineIds.has(line.id)),
  [failedCreditPaymentlineIds, paymentlines]);

  const resolvePaymentlineChargeAmount = useCallback((orderId: number, paymentlineId: number): number | null => {
    const paymentline = paymentlines.find((line) => line.id === paymentlineId);
    if (!paymentline) {
      return null;
    }
    const order = activeOrdersRef.current.find((entry) => entry.id === orderId) ?? null;
    const visibleLines = paymentlines.filter((line) => line.status === 'new' || (line.status === 'done' && !line.canceledAt));
    const billableBase = roundMoney(visibleLines.reduce((sum, line) => sum + line.amount, 0));
    const globalTipTotal = resolveTip(order?.tipInputMode ?? null, order?.tipInputValue ?? null, billableBase);
    const doneTipCollected = roundMoney(
      visibleLines
        .filter((line) => line.status === 'done' && !line.canceledAt)
        .reduce((sum, line) => sum + (line.globalTipCollected ?? 0), 0),
    );
    const openLines = visibleLines.filter((line) => line.status === 'new').sort((left, right) => left.id - right.id);
    const shares = allocateGlobalTip(
      roundMoney(globalTipTotal - doneTipCollected),
      openLines.map((line) => line.amount),
    );
    const openIndex = openLines.findIndex((line) => line.id === paymentlineId);
    const globalShare = openIndex >= 0 ? (shares[openIndex] ?? 0) : 0;
    const lineTip = resolveTip(paymentline.tipInputMode ?? null, paymentline.tipInputValue ?? null, paymentline.amount);
    return roundMoney(paymentline.amount + lineTip + globalShare);
  }, [paymentlines]);

  useEffect(() => {
    setFailedCreditPaymentlineIds((previous) => {
      if (previous.size === 0) {
        return previous;
      }
      const openById = new Map(
        paymentlines
          .filter((line) => line.status === 'new')
          .map((line) => [line.id, line] as const),
      );
      let changed = false;
      const next = new Set<number>();
      previous.forEach((id) => {
        const line = openById.get(id);
        if (!line) {
          changed = true;
          return;
        }
        if (line.hasFailedCreditCharge === false) {
          changed = true;
          return;
        }
        if (line.status === 'new') {
          next.add(id);
        } else {
          changed = true;
        }
      });
      if (!changed && next.size === previous.size) {
        return previous;
      }
      return next;
    });
  }, [paymentlines]);

  // Fires the take-away green-fire bon on-device at a full close; dine-in orders are a no-op.
  const fireTakeAwayKitchenPrint = useCallback((orderId: number) => {
    const order = activeOrdersRef.current.find((entry) => entry.id === orderId);
    if (!order) {
      logToBackend('take-away-kitchen-print', 'trigger skipped: order not found in activeOrdersRef', {
        orderId,
        activeOrderIds: activeOrdersRef.current.map((entry) => entry.id),
      }, 'warn');
      if (!employeeSession) {
        logToBackend('take-away-kitchen-print', 'trigger fallback skipped: missing employee session', {
          orderId,
        }, 'warn');
        return;
      }
      void ordersService.getById(tenantId, orderId, employeeSession)
        .then(async (fetchedOrder) => {
          if (fetchedOrder.orderType !== 'take_away') {
            logToBackend('take-away-kitchen-print', 'trigger fallback skipped: fetched order is not take-away', {
              orderId,
              orderType: fetchedOrder.orderType,
              orderCode: fetchedOrder.orderCode,
            }, 'info');
            return;
          }
          const fetchedLines = await orderlinesService.listForOrder(tenantId, orderId, employeeSession);
          logToBackend('take-away-kitchen-print', 'trigger fallback dispatch', {
            orderId,
            orderCode: fetchedOrder.orderCode,
            orderType: fetchedOrder.orderType,
            lineCount: fetchedLines.length,
            productCount: posProductsRef.current.length,
          }, 'warn');
          void onTakeAwayKitchenPrintRef.current?.(fetchedOrder, fetchedLines, posProductsRef.current);
        })
        .catch((error: unknown) => {
          logToBackend('take-away-kitchen-print', 'trigger fallback failed: could not load order', {
            orderId,
            message: error instanceof Error ? error.message : String(error),
          }, 'error');
        });
      return;
    }
    if (order.orderType !== 'take_away') {
      logToBackend('take-away-kitchen-print', 'trigger skipped: non take-away order', {
        orderId,
        orderType: order.orderType,
        orderCode: order.orderCode,
      }, 'info');
      return;
    }
    logToBackend('take-away-kitchen-print', 'trigger dispatch', {
      orderId,
      orderCode: order.orderCode,
      orderType: order.orderType,
      lineCount: orderlinesRef.current.length,
      productCount: posProductsRef.current.length,
    }, 'info');
    void onTakeAwayKitchenPrintRef.current?.(order, orderlinesRef.current, posProductsRef.current);
  }, [tenantId, employeeSession]);
  const pendingCreditChargeRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const startingCreditPaymentlineIdsRef = useRef<Set<number>>(new Set());

  const getBillableTotalForOrder = useCallback((order: OrderView | null | undefined, lines: OrderlineView[], clampNegativeToZero = true): number => {
    const isTakeAway = order?.orderType === 'take_away';
    const cartTotal = lines
      .filter((line) => line.status !== 'pending' || isTakeAway)
      .reduce((sum, line) => sum + line.unitPrice * line.quantity - (line.discountInput ?? 0), 0);
    const discount = calculateOrderDiscount(cartTotal, order?.discountMode, order?.discountInput);
    const total = cartTotal - discount;
    return roundMoney(clampNegativeToZero ? Math.max(0, total) : total);
  }, []);

  const getPaidTotalForPaymentlines = useCallback((lines: PaymentlineView[]): number => roundMoney(
    lines
      .filter((line) => line.status === 'done' && !line.canceledAt)
      .reduce((sum, line) => sum + line.amount, 0),
  ), []);

  const hasBillableOrderlines = useCallback((order: OrderView | null | undefined, lines: OrderlineView[]): boolean => {
    const isTakeAway = order?.orderType === 'take_away';
    return lines.some((line) => line.quantity > 0 && (line.status !== 'pending' || isTakeAway));
  }, []);

  // Resolve the open order by explicit id first (take-away/list pick), else by table (dine-in tap).
  // The table fallback needs a real table id: table_pk is NULL for take-away, so a null id
  // would wrongly resolve to an arbitrary TA order (list stuck on a TA detail).
  const selectedTableOrder = useMemo(() => {
    if (selectedOrderId !== null) {
      return activeOrders.find((entry) => entry.id === selectedOrderId) ?? null;
    }
    if (selectedOrderTableId !== null) {
      const direct = activeOrders.find((entry) => entry.tableId === selectedOrderTableId) ?? null;
      if (direct) {
        return direct;
      }
      const mergeLink = activeMergeLinks.find((link) => link.tablePk === selectedOrderTableId);
      if (!mergeLink) {
        return null;
      }
      return activeOrders.find((entry) => entry.id === mergeLink.orderPk) ?? null;
    }
    return null;
  }, [activeOrders, activeMergeLinks, selectedOrderId, selectedOrderTableId]);
  const resolvedSelectedOrderId = selectedTableOrder?.id ?? null;
  const isPaymentScreenOpen =
    paymentScreenOrderId !== null && resolvedSelectedOrderId === paymentScreenOrderId;

  const setIsPaymentScreenOpen = useCallback((open: boolean) => {
    if (!open) {
      setPaymentScreenOrderId(null);
      return;
    }
    if (resolvedSelectedOrderId !== null) {
      setPaymentScreenOrderId(resolvedSelectedOrderId);
    }
  }, [resolvedSelectedOrderId]);

  const hasPendingCreditPaymentline = isPaymentScreenOpen
    && paymentlines.some((line) => line.hasPendingCreditCharge === true);

  useEffect(() => {
    if (!employeeSession || paymentScreenOrderId === null || !hasPendingCreditPaymentline) {
      return undefined;
    }
    let cancelled = false;
    const orderId = paymentScreenOrderId;
    const recoverPendingCredit = async () => {
      if (paymentRecoveryInFlightRef.current) {
        return;
      }
      paymentRecoveryInFlightRef.current = true;
      try {
        const refreshed = await paymentsService.listForOrder(tenantId, orderId, employeeSession);
        if (cancelled) {
          return;
        }
        const pendingIds = new Set(
          paymentlinesRef.current
            .filter((line) => line.orderId === orderId && line.hasPendingCreditCharge)
            .map((line) => line.id),
        );
        setPaymentlines(refreshed);
        setLockedCreditPaymentlineIds((previous) => {
          const next = new Set(previous);
          pendingIds.forEach((paymentlineId) => {
            if (refreshed.some((line) => line.id === paymentlineId && line.hasPendingCreditCharge)) {
              next.add(paymentlineId);
            } else {
              next.delete(paymentlineId);
            }
          });
          return next;
        });
        setFailedCreditPaymentlineIds(
          new Set(refreshed.filter((line) => line.hasFailedCreditCharge).map((line) => line.id)),
        );
      } catch {
        // The next interval retries; retain the processing state until the backend confirms it.
      } finally {
        paymentRecoveryInFlightRef.current = false;
      }
    };
    const interval = window.setInterval(() => { void recoverPendingCredit(); }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [employeeSession, hasPendingCreditPaymentline, paymentScreenOrderId, tenantId]);

  // Load active payment terminals once per session so the POS payment-method picker can offer pinpads.
  useEffect(() => {
    if (!employeeSession) {
      setPosTerminals([]);
      return;
    }
    let cancelled = false;
    void creditPaymentsService
      .listTerminals(tenantId, employeeSession)
      .then((terminals) => {
        if (!cancelled) {
          setPosTerminals(terminals);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPosTerminals([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [employeeSession, tenantId]);

  const loadActiveOrders = useCallback(async (): Promise<OrderView[]> => {
    if (!employeeSession) {
      setActiveOrders([]);
      return [];
    }

    setIsLoadingOrders(true);
    try {
      const list = await ordersService.listActiveOpen(tenantId, employeeSession);
      setActiveOrders(list);
      return list;
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      } else {
        setError('Failed loading orders');
      }
      return [];
    } finally {
      setIsLoadingOrders(false);
    }
  }, [employeeSession, setError, tenantId]);

  const loadMergeLinks = useCallback(async (): Promise<void> => {
    if (!employeeSession) {
      setActiveMergeLinks([]);
      return;
    }
    try {
      const links = await mergeService.getActiveMergeLinks(tenantId, employeeSession);
      setActiveMergeLinks(links);
    } catch {
      // non-critical — floor view just shows plain table numbers
    }
  }, [employeeSession, tenantId]);

  const handleUpdateDiners = useCallback(async (orderId: number, dinersCount: number) => {
    if (!employeeSession || updateDinersInFlightRef.current) return;
    updateDinersInFlightRef.current = true;
    setIsUpdatingDiners(true);
    try {
      await ordersService.updateDinersCount(tenantId, orderId, dinersCount, employeeSession);
    } catch (updateError: unknown) {
      if (updateError instanceof Error) setError(updateError.message);
      else setError('Failed updating diners count');
    } finally {
      updateDinersInFlightRef.current = false;
      setIsUpdatingDiners(false);
    }
  }, [employeeSession, setError, tenantId]);

  // Summary: Toggle the per-order card-deposit (פיקדון) flag; SSE-driven, single in-flight guard.
  const handleToggleOrderDeposit = useCallback(async (orderId: number, hasDeposit: boolean) => {
    if (!employeeSession || updateDepositInFlightRef.current) return;
    updateDepositInFlightRef.current = true;
    try {
      await ordersService.updateOrderDeposit(tenantId, orderId, hasDeposit, employeeSession);
    } catch (updateError: unknown) {
      setError(updateError instanceof Error ? updateError.message : 'Failed updating deposit');
    } finally {
      updateDepositInFlightRef.current = false;
    }
  }, [employeeSession, setError, tenantId]);

  // Architectural summary: Toggle VIP by sending the explicit value; state arrives via SSE.
  const handleToggleOrderVip = useCallback(async (orderId: number, isVip: boolean) => {
    if (!employeeSession || toggleVipInFlightRef.current) return;
    toggleVipInFlightRef.current = true;
    try {
      await ordersService.updateOrderVip(tenantId, orderId, isVip, employeeSession);
    } catch (updateError: unknown) {
      if (updateError instanceof Error) setError(updateError.message);
      else setError('Failed updating VIP status');
    } finally {
      toggleVipInFlightRef.current = false;
    }
  }, [employeeSession, setError, tenantId]);

  const handleOpenOrderWithDiners = useCallback(async (dinersCount: number): Promise<boolean> => {
    if (!employeeSession || selectedOrderTableId === null) {
      return false;
    }

    setIsOpeningOrder(true);
    setError(null);
    try {
      await ordersService.openForTable(tenantId, selectedOrderTableId, employeeSession, stationaryName);
      // Resolve the order id with bounded retries to avoid false-not-found right after open.
      let resolvedOrderId: number | null = null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const orders = await loadActiveOrders();
        const resolvedOrder = orders.find((o) => o.tableId === selectedOrderTableId);
        if (resolvedOrder) {
          resolvedOrderId = resolvedOrder.id;
          break;
        }
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 120));
        }
      }
      if (resolvedOrderId === null) {
        setError('Opened table but failed resolving order for diners update');
        return false;
      }
      await ordersService.updateDinersCount(tenantId, resolvedOrderId, dinersCount, employeeSession);
      notifications.show({ color: 'blue', message: 'תזכורת: יש לקחת כרטיס אשראי כפיקדון', autoClose: 3000 });
      await loadActiveOrders();
      setSelectedOrderId(resolvedOrderId);
      return true;
    } catch (openError: unknown) {
      if (openError instanceof Error) {
        setError(openError.message);
      } else {
        setError('Failed opening order');
      }
      return false;
    } finally {
      setIsOpeningOrder(false);
    }
  }, [employeeSession, loadActiveOrders, selectedOrderTableId, setError, stationaryName, tenantId]);

  // Summary: Opens a tableless take-away/quick-charge/kiosk dine-in order and enters it.
  const handleOpenTakeAway = useCallback(async (
    variant: 'take_away' | 'quick_charge' | 'kiosk_dine_in' = 'take_away',
  ): Promise<number | null> => {
    if (!employeeSession || isOpeningOrder) {
      return null;
    }

    setIsOpeningOrder(true);
    setError(null);
    try {
      const before = new Set(activeOrdersRef.current.map((o) => o.id));
      if (variant === 'quick_charge') {
        await ordersService.openQuickCharge(tenantId, employeeSession, stationaryName);
      } else if (variant === 'kiosk_dine_in') {
        await ordersService.openKioskDineIn(tenantId, employeeSession, stationaryName);
      } else {
        await ordersService.openTakeAway(tenantId, employeeSession, stationaryName);
      }
      // Seed from the order.opened SSE the tab already receives (Rule 23/27): poll the in-memory
      // activeOrdersRef, not a GET. One GET only as a fallback if the event is missed.
      const codePrefix = variant === 'quick_charge'
        ? QUICK_CHARGE_CODE_PREFIX
        : variant === 'kiosk_dine_in'
          ? KIOSK_DINE_IN_CODE_PREFIX
          : 'TA_';
      const findFresh = (list: OrderView[]) =>
        list.find((o) => !before.has(o.id)
          && (o.orderCode?.startsWith(codePrefix) ?? false)) ?? null;
      let fresh: OrderView | null = null;
      for (let attempt = 0; attempt < 10 && !fresh; attempt += 1) {
        fresh = findFresh(activeOrdersRef.current);
        if (!fresh) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
      if (!fresh) {
        fresh = findFresh(await loadActiveOrders());
      }
      if (!fresh) {
        setError('Opened take-away but failed resolving the new order');
        return null;
      }
      setSelectedOrderTableId(null);
      setSelectedOrderTableLabel(null);
      setPaymentScreenOrderId(null);
      setIsReceiptScreenOpen(false);
      // QC lands on payment via the shared re-entry effect (same path as an orders-list tap).
      setSelectedOrderId(fresh.id);
      return fresh.id;
    } catch (openError: unknown) {
      setError(openError instanceof Error ? openError.message : 'Failed opening take-away order');
      return null;
    } finally {
      setIsOpeningOrder(false);
    }
  }, [activeOrdersRef, employeeSession, isOpeningOrder, loadActiveOrders, setError, stationaryName, tenantId]);

  // Drawer id: a sole billable cash-drawer printer wins up-front, else the device fixed drawer.
  const resolveValidFixedDrawerId = useCallback(async (): Promise<number | undefined> => {
    if (!employeeSession) return undefined;
    try {
      const printers = await printersService.listActive(tenantId, employeeSession);
      return resolveBillableDrawerId(printers) ?? undefined;
    } catch {
      return undefined;
    }
  }, [tenantId, employeeSession]);

  // Self-heal a stale-open order the server already closed: drop from floor + refresh merge links.
  const selfHealClosedOrder = useCallback((orderId: number) => {
    setActiveOrders((previous) => removeById(previous, orderId));
    void loadMergeLinks();
  }, [loadMergeLinks]);

  // Close attributing cash to a drawer; 409 shows the picker, 404 already-closed self-heals.
  const closeOrderWithDrawer = useCallback(async (
    orderId: number,
    onClosed: (usedDrawerId?: number) => void,
    options?: { onAlreadyClosed?: () => void },
  ): Promise<void> => {
    if (!employeeSession) {
      return;
    }
    const attempt = async (drawerId?: number): Promise<void> => {
      try {
        await ordersService.close(tenantId, orderId, employeeSession, drawerId);
        onClosed(drawerId);
      } catch (error: unknown) {
        if (error instanceof CashDrawerSelectionRequiredError) {
          pendingCloseRetryRef.current = (picked: number) => attempt(picked);
          setCashDrawerPrompt({ mode: 'close', orderId, drawers: error.drawers });
          return;
        }
        if (error instanceof OrderAlreadyClosedError) {
          selfHealClosedOrder(orderId);
          options?.onAlreadyClosed?.();
          return;
        }
        if (error instanceof Error) {
          notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 6000 });
        }
      }
    };
    await attempt(await resolveValidFixedDrawerId());
  }, [employeeSession, tenantId, resolveValidFixedDrawerId, selfHealClosedOrder]);

  const handleCloseOrder = useCallback(async (orderId: number): Promise<boolean> => {
    // Ref-guard same-tick double-taps (button disable lags a render → "already closed" 404s).
    if (!employeeSession || closeOrderInFlightRef.current) {
      return false;
    }
    closeOrderInFlightRef.current = true;

    setIsClosingOrder(true);
    setError(null);
    let closed = false;
    try {
      await closeOrderWithDrawer(orderId, () => {
        closed = true;
        setPaymentScreenOrderId(null);
        setPaymentlines([]);
        void Promise.all([loadActiveOrders(), loadMergeLinks()]);
      }, {
        // Already closed elsewhere counts as released: let the caller leave to אזורים.
        onAlreadyClosed: () => {
          closed = true;
          setPaymentScreenOrderId(null);
          setPaymentlines([]);
        },
      });
    } finally {
      setIsClosingOrder(false);
      closeOrderInFlightRef.current = false;
    }
    return closed;
  }, [employeeSession, setError, closeOrderWithDrawer, loadActiveOrders, loadMergeLinks]);

  useEffect(() => {
    if (!employeeSession || !activeSession) {
      setActiveOrders([]);
      initialOrdersLoadedForSessionRef.current = null;
      return;
    }
    if (initialOrdersLoadedForSessionRef.current === activeSession.id) {
      return;
    }
    initialOrdersLoadedForSessionRef.current = activeSession.id;
    void loadActiveOrders();
    void loadMergeLinks();
  }, [employeeSession, activeSession, loadActiveOrders, loadMergeLinks]);

  const loadPosProducts = useCallback(async () => {
    if (!employeeSession) {
      setPosProducts([]);
      return;
    }
    setIsLoadingPosProducts(true);
    try {
      const list = await productsService.listForPos(tenantId, employeeSession);
      setPosProducts(list);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      }
    } finally {
      setIsLoadingPosProducts(false);
    }
  }, [employeeSession, setError, tenantId]);

  // Architectural summary: Loads active categories for the POS menu rail (any employee role).
  const loadPosCategories = useCallback(async () => {
    if (!employeeSession) {
      setPosCategories([]);
      return;
    }
    try {
      const list = await categoriesService.listForPos(tenantId, employeeSession);
      setPosCategories(list);
    } catch (loadError: unknown) {
      if (loadError instanceof Error) {
        setError(loadError.message);
      }
    }
  }, [employeeSession, setError, tenantId]);

  const loadOrderlines = useCallback(async (orderId: number): Promise<OrderlineView[]> => {
    if (!employeeSession) {
      setOrderlines([]);
      orderlinesRef.current = [];
      loadedOrderlinesOrderIdRef.current = null;
      setOrderlinesLoadedOrderId(null);
      return [];
    }
    setIsLoadingOrderlines(true);
    try {
      const list = await orderlinesService.listForOrder(tenantId, orderId, employeeSession);
      setOrderlines(list);
      orderlinesRef.current = list;
      loadedOrderlinesOrderIdRef.current = orderId;
      setOrderlinesLoadedOrderId(orderId);
      return list;
    } catch (loadError: unknown) {
      // A failed load must not poison the guard: reset so the next open re-fetches, and drop any
      // stale lines rather than show another order's cart.
      loadedOrderlinesOrderIdRef.current = null;
      setOrderlinesLoadedOrderId(null);
      setOrderlines([]);
      orderlinesRef.current = [];
      if (loadError instanceof Error) {
        setError(loadError.message);
      }
      return [];
    } finally {
      setIsLoadingOrderlines(false);
    }
  }, [employeeSession, setError, tenantId]);

  const hydratePaymentlinesForOrder = useCallback(async (
    orderId: number,
    options?: { forReceiptScreen?: boolean; tipOnly?: boolean },
  ): Promise<PaymentlineView[]> => {
    if (!employeeSession) {
      return [];
    }
    setIsPreparingPaymentline(true);
    try {
      const existing = await paymentsService.listForOrder(tenantId, orderId, employeeSession);
      const order = activeOrdersRef.current.find((entry) => entry.id === orderId) ?? null;
      const linesSnapshot = orderlinesRef.current;
      // If orderlines are momentarily not hydrated on this device, use the server order total as a stable fallback.
      const billableTotal = linesSnapshot.length > 0
        ? getBillableTotalForOrder(order, linesSnapshot, false)
        : roundMoney(order?.totalAmount ?? 0);
      const paidTotal = getPaidTotalForPaymentlines(existing);
      const existingOverage = billableTotal < 0 ? 0 : roundMoney(paidTotal - billableTotal);
      const hasOpenLine = existing.some((line) => line.status === 'new');
      // Fully-discounted = fired lines discounted to 0; a pending-only order must NOT zero-close.
      const isFullyDiscountedCloseCandidate = billableTotal === 0 && hasBillableOrderlines(order, orderlinesRef.current);
      if (isFullyDiscountedCloseCandidate) {
        logToBackend('fully-discounted-close', 'payment screen hydrated', {
          orderId,
          forReceiptScreen: Boolean(options?.forReceiptScreen),
          tipOnly: Boolean(options?.tipOnly),
          orderlineCount: orderlinesRef.current.length,
          billableTotal,
          paidTotal,
          existingOverage,
          paymentlines: existing.map((line) => ({
            id: line.id,
            status: line.status,
            amount: line.amount,
            tipInputMode: line.tipInputMode,
            tipInputValue: line.tipInputValue,
            globalTipCollected: line.globalTipCollected,
          })),
        });
      }
      if (existing.length > 0) {
        setPaymentlines(existing);
        // Keep a live TOP lock received over SSE while reopening the payment page. TOP does not
        // have a durable credit attempt until the native reader returns an approved witness.
        setLockedCreditPaymentlineIds((previous) => {
          const next = new Set(previous);
          existing
            .filter((line) => line.hasPendingCreditCharge)
            .forEach((line) => next.add(line.id));
          return next;
        });
        // Authoritative refetch: reseed the failed set to durable failures only (clears stale).
        setFailedCreditPaymentlineIds(
          new Set(existing.filter((line) => line.hasFailedCreditCharge).map((line) => line.id)),
        );
      }
      if (existingOverage > 0) {
        // Same as zero-total: the overage modal is payment-screen only; keep the bill viewable.
        if (!options?.forReceiptScreen) {
          setPaymentOveragePrompt({ orderId, overpaidAmount: existingOverage });
        }
        return existing;
      }
      // Close confirmation is payment-screen only; it covers both a true zero-total ticket and
      // an exactly settled order whose final pending line was canceled by a transfer.
      const closeReason = billableTotal === 0 ? 'zero_total' : 'settled';
      // Backend settled the freshly created line outright: offer close (payment screen only).
      const offerCloseIfSettled = (
        next: PaymentlineView[],
        paymentline: PaymentlineView,
        reason: PaymentClosePrompt['reason'],
      ): void => {
        if (options?.forReceiptScreen || options?.tipOnly) return;
        if (paymentline.status !== 'done' || next.some((line) => line.status === 'new')) return;
        logToBackend('fully-discounted-close', 'showing close confirmation after settlement', { orderId, reason });
        setPaymentZeroTotalPrompt({ orderId, reason });
      };
      const shouldStartClose = !options?.forReceiptScreen
        && !options?.tipOnly
        && existing.length > 0
        && !hasOpenLine
        && existingOverage === 0;
      if (shouldStartClose) {
        if (isFullyDiscountedCloseCandidate) {
          logToBackend('fully-discounted-close', 'showing close confirmation from settled paymentlines', { orderId, closeReason });
        }
        setPaymentZeroTotalPrompt({ orderId, reason: closeReason });
        return existing;
      }
      const hasTipOnOpenLine = existing.some(
        (line) => line.status === 'new'
          && resolveTip(line.tipInputMode ?? null, line.tipInputValue ?? null, line.amount) > 0,
      );
      const hasOrderTip = resolveTip(order?.tipInputMode ?? null, order?.tipInputValue ?? null, billableTotal) > 0;
      const shouldAutoSettleFullyDiscountedOrder = !options?.forReceiptScreen
        && !options?.tipOnly
        && billableTotal === 0
        && hasBillableOrderlines(order, orderlinesRef.current)
        && hasOpenLine
        && !hasTipOnOpenLine
        && !hasOrderTip;
      if (shouldAutoSettleFullyDiscountedOrder) {
        logToBackend('fully-discounted-close', 'requesting canonical zero-line settlement', { orderId });
        const paymentline = await paymentsService.createForOrder(tenantId, orderId, employeeSession);
        const next = mergeById(existing, paymentline);
        setPaymentlines((previous) => mergeById(previous, paymentline));
        logToBackend('fully-discounted-close', 'canonical zero-line settlement response', {
          orderId,
          paymentlineId: paymentline.id,
          status: paymentline.status,
          amount: paymentline.amount,
          remainingNewPaymentlineIds: next.filter((line) => line.status === 'new').map((line) => line.id),
        });
        offerCloseIfSettled(next, paymentline, 'zero_total');
        return next;
      }
      // Empty order: mirror the backend ORDER_TOTAL_ZERO 409 guard; discounted/canceled/tip-only pass above.
      if (
        !options?.forReceiptScreen
        && !options?.tipOnly
        && existing.length === 0
        && billableTotal === 0
        && !hasBillableOrderlines(order, orderlinesRef.current)
      ) {
        notifications.show({
          color: 'blue',
          title: 'אין מה לחייב',
          message: 'הוסף פריטים להזמנה לפני מעבר לתשלום',
          autoClose: 3000,
        });
        setPaymentScreenOrderId(null);
        return [];
      }
      if (!hasOpenLine) {
        const paymentline = await paymentsService.createForOrder(
          tenantId,
          orderId,
          employeeSession,
          options?.tipOnly ? { tipOnly: true } : undefined,
        );
        const next = mergeById(existing, paymentline);
        setPaymentlines((previous) => mergeById(previous, paymentline));
        offerCloseIfSettled(next, paymentline, closeReason);
        return next;
      }
      return existing;
    } catch (paymentError: unknown) {
      if (paymentError instanceof Error) {
        logToBackend('fully-discounted-close', 'payment screen hydration failed', { orderId, message: paymentError.message }, 'error');
      }
      if (paymentError instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(paymentError.message),
          autoClose: 3000,
        });
        setPaymentScreenOrderId(null);
      }
      return [];
    } finally {
      setIsPreparingPaymentline(false);
    }
  }, [employeeSession, getBillableTotalForOrder, getPaidTotalForPaymentlines, hasBillableOrderlines, tenantId]);

  const handleOpenPaymentScreen = useCallback(async (orderId: number, options?: { tipOnly?: boolean }) => {
    // Block only when nothing is billable at all. A fully discounted fired order may still have
    // billable lines with a zero total, and that should be closable from the payment screen.
    const order = activeOrders.find((entry) => entry.id === orderId);
    const hasBillable = hasBillableOrderlines(order, orderlinesRef.current) || (order?.totalAmount ?? 0) > 0;
    const tipOnly = options?.tipOnly === true;
    const canStartTipOnly = tipOnly && orderlinesRef.current.length === 0;
    const canResolveZeroTotal = !tipOnly && order?.totalAmount === 0;
    if (!order || (!hasBillable && !canStartTipOnly && !canResolveZeroTotal)) {
      notifications.show({
        color: 'blue',
        title: 'אין מה לחייב',
        message: 'הוסף פריטים להזמנה לפני מעבר לתשלום',
        autoClose: 3000,
      });
      return;
    }
    setPaymentlines([]);
    setPaymentOveragePrompt(null);
    setPaymentZeroTotalPrompt(null);
    setPaymentScreenOrderId(orderId);
    setIsReceiptScreenOpen(false);
    await hydratePaymentlinesForOrder(orderId, { tipOnly });
  }, [activeOrders, hasBillableOrderlines, hydratePaymentlinesForOrder]);

  // Summary: Prepares a direct Quick Charge paymentline; refunds reuse general-item 2FA and tip charge/refund use tip accounting.
  const applyQuickChargeAmount = useCallback(async (
    orderId: number,
    amount: number,
    mode: 'charge' | 'refund' | 'service' | 'service_refund' = 'charge',
    refundCode?: string,
  ): Promise<number | null> => {
    if (!employeeSession) {
      return null;
    }
    if (mode !== 'service' && mode !== 'service_refund' && amount > 0) {
      const signedAmount = mode === 'refund' ? -Math.abs(amount) : Math.abs(amount);
      const line = refundCode
        ? await orderlinesService.addGeneralItem(tenantId, orderId, signedAmount, 1, employeeSession, refundCode)
        : await orderlinesService.addGeneralItem(tenantId, orderId, signedAmount, 1, employeeSession);
      await orderlinesService.updateNote(
        tenantId,
        orderId,
        line.id,
        mode === 'refund' ? 'זיכוי מהיר' : 'חיוב מהיר',
        employeeSession,
      );
      await loadOrderlines(orderId);
      const prepared = await hydratePaymentlinesForOrder(orderId);
      return prepared.find((paymentline) => paymentline.status === 'new')?.id ?? null;
    }

    const prepared = await hydratePaymentlinesForOrder(orderId, { tipOnly: true });
    const paymentline = prepared.find((entry) => entry.status === 'new') ?? null;
    if (!paymentline) {
      return null;
    }
    if ((mode === 'service' || mode === 'service_refund') && amount > 0) {
      const signedTipValue = mode === 'service_refund' ? -Math.abs(amount) : Math.abs(amount);
      await paymentsService.setPaymentlineTip(
        tenantId,
        paymentline.id,
        { tipInputMode: 'value', tipInputValue: signedTipValue },
        employeeSession,
      );
      setPaymentlines((previous) => previous.map((entry) => (
        entry.id === paymentline.id
          ? { ...entry, tipInputMode: 'value' as const, tipInputValue: signedTipValue }
          : entry
      )));
    }
    return paymentline.id;
  }, [employeeSession, hydratePaymentlinesForOrder, loadOrderlines, tenantId]);

  // Summary: Cancels an empty quick-charge order — exit payment, deselect, release via TA-exit.
  const cancelQuickCharge = useCallback(async (orderId: number) => {
    setQuickChargeAmountOrderId(null);
    setPaymentScreenOrderId(null);
    setPaymentlines([]);
    setIsReceiptScreenOpen(false);
    setSelectedOrderId(null);
    setSelectedOrderTableId(null);
    setSelectedOrderTableLabel(null);
    setOrderlines([]);
    orderlinesRef.current = [];
    if (employeeSession) {
      await ordersService.closeTakeAwayOnExit(tenantId, orderId, employeeSession);
    }
  }, [employeeSession, tenantId]);

  const clearQuickChargeAmountPrompt = useCallback(() => setQuickChargeAmountOrderId(null), []);

  // Every QC selection lands on payment before paint; an empty QC (re)opens the amount modal.
  const selectedOrderCode = selectedTableOrder?.orderCode ?? null;
  useLayoutEffect(() => {
    if (resolvedSelectedOrderId === null) {
      lastQuickChargeLandedRef.current = null;
      return undefined;
    }
    if (!isQuickChargeOrderCode(selectedOrderCode)) {
      return undefined;
    }
    if (lastQuickChargeLandedRef.current === resolvedSelectedOrderId) {
      return undefined;
    }
    lastQuickChargeLandedRef.current = resolvedSelectedOrderId;
    const orderId = resolvedSelectedOrderId;
    setIsReceiptScreenOpen(false);
    setPaymentlines([]);
    setPaymentScreenOrderId(orderId);
    let cancelled = false;
    void (async () => {
      if (!employeeSession) return;
      try {
        const [lines, payments] = await Promise.all([
          loadOrderlines(orderId),
          paymentsService.listForOrder(tenantId, orderId, employeeSession),
        ]);
        if (cancelled) return;
        if (lines.length === 0 && payments.length === 0) {
          setQuickChargeAmountOrderId(orderId);
        } else {
          await hydratePaymentlinesForOrder(orderId);
        }
      } catch (error) {
        if (cancelled) return;
        lastQuickChargeLandedRef.current = null;
        notifications.show({ color: 'red', message: toHebrewApiErrorMessage((error as Error)?.message) });
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedSelectedOrderId, selectedOrderCode, employeeSession, tenantId, loadOrderlines, hydratePaymentlinesForOrder]);

  const handlePayAndCloseIfDone = useCallback(async (orderId: number, paymentlineId?: number) => {
    if (!employeeSession) {
      return;
    }
    if (paymentMutationInFlightRef.current) {
      return;
    }
    // Fully-discounted order: all paymentlines are already 'done' (₪0 done line was auto-created).
    // Skip markDone and go straight to close.
    const newLine = typeof paymentlineId === 'number'
      ? paymentlines.find((line) => line.id === paymentlineId && line.status === 'new')
      : paymentlines.find((line) => line.status === 'new');
    if (!newLine) {
      // Ignore canceled (split-parent/reversed) lines: fully paid = every active line is done.
      const activeLines = paymentlines.filter((line) => line.status === 'new' || line.status === 'done');
      const allDone = activeLines.length > 0 && activeLines.every((line) => line.status === 'done');
      if (allDone) {
        setIsMarkingPaymentDone(true);
        try {
          // Settled close: resolve the drawer so cash tenders keep attribution (M3).
          await closeOrderWithDrawer(orderId, (usedDrawerId) => {
            fireTakeAwayKitchenPrint(orderId);
            setPaymentScreenOrderId(null);
            setPaymentlines([]);
            setSelectedOrderTableId(null);
            setSelectedOrderTableLabel(null);
            setProductSearch('');
            setRecentResultIds([]);
            void Promise.all([loadActiveOrders(), loadMergeLinks()]);
            onAfterFullPayment();
            onReceiptAutoPrintRef.current?.(orderId, { cashDrawerPrinterId: usedDrawerId });
          });
        } finally {
          setIsMarkingPaymentDone(false);
        }
      }
      return;
    }

    const order = activeOrdersRef.current.find((entry) => entry.id === orderId) ?? null;
    const billableTotal = getBillableTotalForOrder(order, orderlinesRef.current);
    const projectedPaidTotal = roundMoney(getPaidTotalForPaymentlines(paymentlines) + newLine.amount);
    const projectedOverage = roundMoney(projectedPaidTotal - billableTotal);
    if (projectedOverage > 0) {
      setPaymentOveragePrompt({
        orderId,
        paymentlineId: newLine.id,
        overpaidAmount: projectedOverage,
      });
      return;
    }

    paymentMutationInFlightRef.current = true;
    setIsMarkingPaymentDone(true);
    try {
      const fixedDrawerId = await resolveValidFixedDrawerId();
      await paymentsService.markDone(tenantId, newLine.id, employeeSession, fixedDrawerId);
      const projected = paymentlines.map((line) =>
        line.id === newLine.id ? { ...line, status: 'done' as const } : line,
      );
      // Ignore canceled (split-parent/reversed) lines: fully paid = every active line is done.
      const activeProjected = projected.filter((line) => line.status === 'new' || line.status === 'done');
      const allDone = activeProjected.length > 0 && activeProjected.every((line) => line.status === 'done');
      // Full close prints the full receipt; splits print via the manual קבלה חלקית button.
      if (allDone) {
        fireTakeAwayKitchenPrint(orderId);
        onReceiptAutoPrintRef.current?.(orderId, { cashDrawerPrinterId: fixedDrawerId });
      }
      if (allDone) {
        setSelectedOrderTableId(null);
        setSelectedOrderTableLabel(null);
        setProductSearch('');
        setRecentResultIds([]);
        void Promise.all([loadActiveOrders(), loadMergeLinks()]);
        onAfterFullPayment();
      }
    } catch (payError: unknown) {
      if (payError instanceof CashDrawerSelectionRequiredError) {
        setCashDrawerPrompt({ mode: 'pay', orderId, paymentlineId: newLine.id, drawers: payError.drawers });
      } else if (payError instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(payError.message),
          autoClose: 3000,
        });
      }
    } finally {
      setIsMarkingPaymentDone(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, paymentlines, tenantId, onAfterFullPayment, loadActiveOrders, loadMergeLinks, getBillableTotalForOrder, getPaidTotalForPaymentlines, resolveValidFixedDrawerId, closeOrderWithDrawer, fireTakeAwayKitchenPrint]);

  const handleSettleCashOverage = useCallback(async (
    orderId: number,
    paymentlineId: number,
    tenderedAmount: number,
    action: 'return_change' | 'add_to_tip',
  ): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsMarkingPaymentDone(true);
    try {
      const fixedDrawerId = await resolveValidFixedDrawerId();
      await paymentsService.settleCashOverage(
        tenantId,
        paymentlineId,
        {
          tenderedAmount,
          action,
          ...(fixedDrawerId != null ? { cashDrawerPrinterId: fixedDrawerId } : {}),
        },
        employeeSession,
      );
      const projected = paymentlines.map((line) =>
        line.id === paymentlineId ? { ...line, status: 'done' as const } : line,
      );
      const activeProjected = projected.filter((line) => line.status === 'new' || line.status === 'done');
      const allDone = activeProjected.length > 0 && activeProjected.every((line) => line.status === 'done');
      if (allDone) {
        fireTakeAwayKitchenPrint(orderId);
        onReceiptAutoPrintRef.current?.(orderId, { cashDrawerPrinterId: fixedDrawerId });
        setPaymentScreenOrderId(null);
        setPaymentlines([]);
        setSelectedOrderTableId(null);
        setSelectedOrderTableLabel(null);
        setProductSearch('');
        setRecentResultIds([]);
        void Promise.all([loadActiveOrders(), loadMergeLinks()]);
        onAfterFullPayment();
      }
      return true;
    } catch (error: unknown) {
      if (error instanceof CashDrawerSelectionRequiredError) {
        setCashDrawerPrompt({
          mode: 'settle-overage',
          orderId,
          paymentlineId,
          tenderedAmount,
          action,
          drawers: error.drawers,
        });
      } else if (error instanceof Error) {
        notifications.show({
          color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000,
        });
      }
      return false;
    } finally {
      setIsMarkingPaymentDone(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, fireTakeAwayKitchenPrint, loadActiveOrders, loadMergeLinks, onAfterFullPayment, paymentlines, resolveValidFixedDrawerId, tenantId]);

  const dismissPaymentOverageToMenu = useCallback(() => {
    setPaymentOveragePrompt(null);
    setPaymentScreenOrderId(null);
  }, []);

  const dismissZeroTotalToMenu = useCallback(() => {
    setPaymentZeroTotalPrompt(null);
    setPaymentScreenOrderId(null);
  }, []);

  const confirmPaymentOverageClose = useCallback(async (): Promise<void> => {
    if (!employeeSession || !paymentOveragePrompt || paymentMutationInFlightRef.current) {
      return;
    }

    paymentMutationInFlightRef.current = true;
    setIsResolvingPaymentOverage(true);
    try {
      const fixedDrawerId = await resolveValidFixedDrawerId();
      await paymentsService.resolveOverpaymentAndClose(
        tenantId,
        paymentOveragePrompt.orderId,
        {
          ...(paymentOveragePrompt.paymentlineId ? { paymentlineId: paymentOveragePrompt.paymentlineId } : {}),
          ...(fixedDrawerId != null ? { cashDrawerPrinterId: fixedDrawerId } : {}),
        },
        employeeSession,
      );
      fireTakeAwayKitchenPrint(paymentOveragePrompt.orderId);
      onReceiptAutoPrintRef.current?.(paymentOveragePrompt.orderId, { cashDrawerPrinterId: fixedDrawerId });
      setPaymentOveragePrompt(null);
      setSelectedOrderTableId(null);
      setSelectedOrderTableLabel(null);
      setProductSearch('');
      setRecentResultIds([]);
      void Promise.all([loadActiveOrders(), loadMergeLinks()]);
      onAfterFullPayment();
    } catch (error: unknown) {
      if (error instanceof CashDrawerSelectionRequiredError && paymentOveragePrompt.paymentlineId) {
        setCashDrawerPrompt({ mode: 'overpay', orderId: paymentOveragePrompt.orderId, paymentlineId: paymentOveragePrompt.paymentlineId, drawers: error.drawers });
      } else if (error instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(error.message),
          autoClose: 3000,
        });
      }
    } finally {
      setIsResolvingPaymentOverage(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, loadActiveOrders, loadMergeLinks, onAfterFullPayment, paymentOveragePrompt, tenantId, resolveValidFixedDrawerId, fireTakeAwayKitchenPrint]);

  const dismissCashDrawerPrompt = useCallback(() => {
    pendingCloseRetryRef.current = null;
    setCashDrawerPrompt(null);
  }, []);

  // Retry the cash close with the chosen drawer; backend reuses it for change + later cash lines on this order.
  const confirmCashDrawerSelection = useCallback(async (drawerId: number): Promise<void> => {
    if (!employeeSession || !cashDrawerPrompt || paymentMutationInFlightRef.current) {
      return;
    }
    const prompt = cashDrawerPrompt;
    paymentMutationInFlightRef.current = true;
    setIsMarkingPaymentDone(true);
    try {
      // Settled-close picker: replay the captured close with the chosen drawer.
      if (prompt.mode === 'close') {
        const retry = pendingCloseRetryRef.current;
        pendingCloseRetryRef.current = null;
        setCashDrawerPrompt(null);
        if (retry) {
          await retry(drawerId);
        }
        return;
      }
      let allDone = true;
      if (prompt.mode === 'overpay') {
        await paymentsService.resolveOverpaymentAndClose(
          tenantId,
          prompt.orderId,
          { paymentlineId: prompt.paymentlineId, cashDrawerPrinterId: drawerId },
          employeeSession,
        );
      } else if (prompt.mode === 'settle-overage') {
        await paymentsService.settleCashOverage(
          tenantId,
          prompt.paymentlineId,
          {
            tenderedAmount: prompt.tenderedAmount,
            action: prompt.action,
            cashDrawerPrinterId: drawerId,
          },
          employeeSession,
        );
        const projected = paymentlines.map((line) =>
          line.id === prompt.paymentlineId ? { ...line, status: 'done' as const } : line,
        );
        const activeProjected = projected.filter((line) => line.status === 'new' || line.status === 'done');
        allDone = activeProjected.length > 0 && activeProjected.every((line) => line.status === 'done');
      } else {
        await paymentsService.markDone(tenantId, prompt.paymentlineId, employeeSession, drawerId);
        const projected = paymentlines.map((line) =>
          line.id === prompt.paymentlineId ? { ...line, status: 'done' as const } : line,
        );
        // Ignore canceled (split-parent/reversed) lines: fully paid = every active line is done.
        const activeProjected = projected.filter((line) => line.status === 'new' || line.status === 'done');
        allDone = activeProjected.length > 0 && activeProjected.every((line) => line.status === 'done');
      }
      setCashDrawerPrompt(null);
      if (allDone) {
        fireTakeAwayKitchenPrint(prompt.orderId);
        setPaymentOveragePrompt(null);
        setSelectedOrderTableId(null);
        setSelectedOrderTableLabel(null);
        setProductSearch('');
        setRecentResultIds([]);
        void Promise.all([loadActiveOrders(), loadMergeLinks()]);
        onAfterFullPayment();
        // Full close via the drawer-pick path must also fire the receipt preview/auto-print.
        onReceiptAutoPrintRef.current?.(prompt.orderId, { cashDrawerPrinterId: drawerId });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(error.message),
          autoClose: 3000,
        });
      }
    } finally {
      setIsMarkingPaymentDone(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, cashDrawerPrompt, paymentlines, tenantId, loadActiveOrders, loadMergeLinks, onAfterFullPayment, fireTakeAwayKitchenPrint]);

  const confirmZeroTotalClose = useCallback(async (): Promise<void> => {
    if (!employeeSession || !paymentZeroTotalPrompt || paymentMutationInFlightRef.current) {
      return;
    }

    paymentMutationInFlightRef.current = true;
    setIsResolvingZeroTotalClose(true);
    try {
      const zeroCloseOrderId = paymentZeroTotalPrompt.orderId;
      // Settled/zero-total close: resolve the drawer so cash keeps attribution (M3).
      await closeOrderWithDrawer(zeroCloseOrderId, (usedDrawerId) => {
        fireTakeAwayKitchenPrint(zeroCloseOrderId);
        setPaymentZeroTotalPrompt(null);
        setPaymentScreenOrderId(null);
        setPaymentlines([]);
        setSelectedOrderTableId(null);
        setSelectedOrderTableLabel(null);
        setProductSearch('');
        setRecentResultIds([]);
        void Promise.all([loadActiveOrders(), loadMergeLinks()]);
        onAfterFullPayment();
        // Zero-total/settled close opens the same post-payment success screen as a normal close.
        onReceiptAutoPrintRef.current?.(zeroCloseOrderId, { cashDrawerPrinterId: usedDrawerId });
      });
    } finally {
      setIsResolvingZeroTotalClose(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, loadActiveOrders, loadMergeLinks, onAfterFullPayment, paymentZeroTotalPrompt, closeOrderWithDrawer, fireTakeAwayKitchenPrint]);

  // Summary: Shared fire-and-forget credit-charge runner: enforces the single-in-flight guards, fires the POST, then locks the processing UI until the SSE resolved event (or a safety timer) releases it.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const beginCreditCharge = useCallback(async (
    paymentlineId: number,
    fire: () => Promise<unknown>,
    options?: { skipLocalLookup?: boolean; alreadyInFlight?: boolean },
  ): Promise<boolean> => {
    if (!employeeSession) {
      return false;
    }
    if (paymentMutationInFlightRef.current && !options?.alreadyInFlight) {
      return false;
    }
    // A just-split paid child is authoritative (server-returned); its local row may lag via SSE.
    if (!options?.skipLocalLookup) {
      const newLine = paymentlines.find((line) => line.id === paymentlineId && line.status === 'new');
      if (!newLine) {
        return false;
      }
    }
    if (
      pendingCreditChargeRef.current.has(paymentlineId)
      || (startingCreditPaymentlineIdsRef.current.has(paymentlineId) && !options?.alreadyInFlight)
    ) {
      return false;
    }

    startingCreditPaymentlineIdsRef.current.add(paymentlineId);
    setLockedCreditPaymentlineIds((previous) => {
      if (previous.has(paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === paymentlineId
          ? { ...line, hasPendingCreditCharge: true }
          : line,
      ),
    );
    clearFailedCreditPaymentline(paymentlineId);
    // Register before the POST: a terminal/provider may publish a declined result before the
    // HTTP request resolves. Registering afterward loses that result and leaves only timeout UI.
    const timer = setTimeout(() => {
      pendingCreditChargeRef.current.delete(paymentlineId);
      setLockedCreditPaymentlineIds((previous) => {
        if (!previous.has(paymentlineId)) {
          return previous;
        }
        const next = new Set(previous);
        next.delete(paymentlineId);
        return next;
      });
      setPaymentlines((previous) =>
        previous.map((line) =>
          line.id === paymentlineId
            ? { ...line, hasPendingCreditCharge: false }
            : line,
        ),
      );
      notifications.show({
        color: 'orange',
        title: 'אין אישור מהסליקה',
        message: 'לא התקבלה תשובה. בדקו את ההזמנה לפני חיוב חוזר כדי למנוע כפל חיוב.',
        autoClose: 3000,
      });
      markPaymentlineAsFailed(paymentlineId);
    }, 60000);
    pendingCreditChargeRef.current.set(paymentlineId, timer);
    startingCreditPaymentlineIdsRef.current.delete(paymentlineId);
    try {
      await fire();
      return true;
    } catch (chargeError: unknown) {
      const pendingTimer = pendingCreditChargeRef.current.get(paymentlineId);
      if (pendingTimer !== undefined) {
        clearTimeout(pendingTimer);
        pendingCreditChargeRef.current.delete(paymentlineId);
      }
      setLockedCreditPaymentlineIds((previous) => {
        if (!previous.has(paymentlineId)) {
          return previous;
        }
        const next = new Set(previous);
        next.delete(paymentlineId);
        return next;
      });
      setPaymentlines((previous) =>
        previous.map((line) =>
          line.id === paymentlineId
            ? { ...line, hasPendingCreditCharge: false }
            : line,
        ),
      );
      if (chargeError instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(chargeError.message),
          autoClose: 3000,
        });
      }
      markPaymentlineAsFailed(paymentlineId);
      return false;
    }
  }, [clearFailedCreditPaymentline, employeeSession, markPaymentlineAsFailed, paymentlines, tenantId]);

  // Summary: Fire-and-forget pinpad credit charge: POST acks (202) after the attempt row is written; the real outcome arrives via the SSE credit.transaction.resolved event, so a dropped HTTP response never loses it.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleChargeCredit = useCallback(async (
    orderId: number,
    paymentlineId: number,
    terminalId: number,
    pinpadId: string,
  ) => {
    void orderId;
    await beginCreditCharge(paymentlineId, () =>
      creditPaymentsService.charge(tenantId, paymentlineId, { terminalId, pinpadId }, employeeSession ?? ''),
    );
  }, [beginCreditCharge, employeeSession, tenantId]);

  // Summary: Manual (card-not-present) credit charge: same fire-and-forget spine as the pinpad path; resolves true so the card-entry popup can close on a clean ack.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleChargeManualCredit = useCallback(async (
    paymentlineId: number,
    payload: ChargeManualCreditRequest,
  ): Promise<boolean> => {
    return beginCreditCharge(paymentlineId, () =>
      creditPaymentsService.chargeManual(tenantId, paymentlineId, payload, employeeSession ?? ''),
    );
  }, [beginCreditCharge, employeeSession, tenantId]);

  // Summary: Run the on-device tap-on-phone charge for an amount; returns the witness or null.
  const runTapOnPhoneBridge = useCallback(async (
    paymentlineId: number,
    amount: number,
  ): Promise<{
    transactionId: string;
    approvalNumber: string | null;
    cardLast4: string | null;
    cardBrand: string | null;
    bridgeOutcome: 'approved';
    bridgeMessage: string | null;
    rawPayload: unknown;
    diagnostics: unknown[];
  } | {
    bridgeOutcome: 'declined' | 'cancelled';
    bridgeMessage: string | null;
    rawPayload: unknown;
    diagnostics: unknown[];
  } | null> => {
    if (amount < 0) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'זיכוי נתמך רק במסוף אשראי רגיל.', autoClose: 3000 });
      return null;
    }
    // Runtime safety guard (Odoo payDevice): re-check the bridge even if the button was shown from stale UI.
    if (!tapOnPhoneBridge.isAvailable()) {
      notifications.show({ color: 'red', title: 'שגיאה', message: 'לא נמצא חיבור למכשיר תשלום.', autoClose: 3000 });
      return null;
    }
    let device;
    try {
      device = await tapOnPhoneBridge.startPayment(amount);
    } catch (bridgeError: unknown) {
      if (bridgeError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(bridgeError.message), autoClose: 3000 });
      }
      return null;
    }
    const diagnostics = tapOnPhoneBridge.getDiagnostics() ?? [];
    const rawPayload = diagnostics.length > 0 ? diagnostics[diagnostics.length - 1] : null;
    if (device.outcome === 'cancelled') {
      logToBackend('tap-on-phone', 'charge-flow: cancelled', { paymentlineId, rawPayload, diagnostics });
      return { bridgeOutcome: 'cancelled', bridgeMessage: device.message, rawPayload, diagnostics };
    }
    if (device.outcome !== 'approved') {
      logToBackend('tap-on-phone', 'charge-flow: not approved, decline toast', { paymentlineId, device, rawPayload, diagnostics }, 'warn');
      notifications.show({ color: 'red', title: 'העסקה נדחתה', message: device.message ?? 'הכרטיס לא אושר', autoClose: 3000 });
      return { bridgeOutcome: 'declined', bridgeMessage: device.message, rawPayload, diagnostics };
    }
    logToBackend('tap-on-phone', 'charge-flow: approved, recording witness', {
      paymentlineId,
      transactionId: device.transactionId,
      approvalNumber: device.approvalNumber,
      cardLast4: device.cardLast4,
      cardBrand: device.cardBrand,
      rawPayload,
      diagnostics,
    });
    return {
      transactionId: device.transactionId,
      approvalNumber: device.approvalNumber,
      cardLast4: device.cardLast4,
      cardBrand: device.cardBrand,
      bridgeOutcome: 'approved',
      bridgeMessage: device.message,
      rawPayload,
      diagnostics,
    };
  }, []);

  // Summary: Tap-on-phone credit charge: runs the native terminal first, then records the approved device witness through the shared credit spine so the backend can verify it by reference.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleChargeTapOnPhone = useCallback(async (
    paymentlineId: number,
  ): Promise<boolean> => {
    if (!employeeSession) {
      return false;
    }
    const newLine = paymentlines.find((line) => line.id === paymentlineId && line.status === 'new');
    if (!newLine) {
      return false;
    }
    if (paymentMutationInFlightRef.current || startingCreditPaymentlineIdsRef.current.has(paymentlineId)) {
      return false;
    }
    startingCreditPaymentlineIdsRef.current.add(paymentlineId);
    setLockedCreditPaymentlineIds((previous) => {
      const next = new Set(previous);
      next.add(paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === paymentlineId
          ? { ...line, hasPendingCreditCharge: true }
          : line,
      ),
    );
    try {
      const { amount: chargeAmount } = await creditPaymentsService.startTapBridge(tenantId, paymentlineId, employeeSession);
      const witness = await runTapOnPhoneBridge(paymentlineId, chargeAmount);
      if (!witness || witness.bridgeOutcome !== 'approved') {
        startingCreditPaymentlineIdsRef.current.delete(paymentlineId);
        setLockedCreditPaymentlineIds((previous) => {
          if (!previous.has(paymentlineId)) {
            return previous;
          }
          const next = new Set(previous);
          next.delete(paymentlineId);
          return next;
        });
        setPaymentlines((previous) =>
          previous.map((line) =>
            line.id === paymentlineId
              ? { ...line, hasPendingCreditCharge: false }
              : line,
          ),
        );
        void creditPaymentsService.releaseTapBridge(tenantId, paymentlineId, employeeSession, {
          bridgeOutcome: witness?.bridgeOutcome,
          bridgeMessage: witness?.bridgeMessage,
          rawPayload: witness?.rawPayload,
          diagnostics: witness?.diagnostics,
        });
        markPaymentlineAsFailed(paymentlineId);
        return false;
      }
      return beginCreditCharge(paymentlineId, () =>
        creditPaymentsService.chargeTap(
          tenantId,
          paymentlineId,
          {
            transactionId: witness.transactionId,
            approvalNumber: witness.approvalNumber,
            cardLast4: witness.cardLast4,
            cardBrand: witness.cardBrand,
            bridgeOutcome: witness.bridgeOutcome,
            bridgeMessage: witness.bridgeMessage,
            rawPayload: witness.rawPayload,
            diagnostics: witness.diagnostics,
          },
          employeeSession ?? '',
        ),
        { alreadyInFlight: true },
      );
    } catch (error: unknown) {
      startingCreditPaymentlineIdsRef.current.delete(paymentlineId);
      setLockedCreditPaymentlineIds((previous) => {
        const next = new Set(previous);
        next.delete(paymentlineId);
        return next;
      });
      setPaymentlines((previous) =>
        previous.map((line) =>
          line.id === paymentlineId
            ? { ...line, hasPendingCreditCharge: false }
            : line,
        ),
      );
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
      markPaymentlineAsFailed(paymentlineId);
      return false;
    }
  }, [beginCreditCharge, employeeSession, markPaymentlineAsFailed, paymentlines, tenantId, runTapOnPhoneBridge]);

  // Summary: Cross-device in-flight lock: an attempt row was written for this paymentline, so freeze it on every device until resolved.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleCreditTransactionPending = useCallback((event: CreditTransactionPendingEvent) => {
    setLockedCreditPaymentlineIds((previous) => {
      if (previous.has(event.paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(event.paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === event.paymentlineId
          ? { ...line, hasPendingCreditCharge: true }
          : line,
      ),
    );
  }, []);

  const handleTapOnPhonePending = useCallback((event: TapOnPhonePendingEvent) => {
    setLockedCreditPaymentlineIds((previous) => {
      if (previous.has(event.paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(event.paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === event.paymentlineId
          ? { ...line, hasPendingCreditCharge: true }
          : line,
      ),
    );
  }, []);

  const handleTapOnPhoneReleased = useCallback((event: TapOnPhoneReleasedEvent) => {
    setLockedCreditPaymentlineIds((previous) => {
      if (!previous.has(event.paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.delete(event.paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === event.paymentlineId
          ? {
              ...line,
              hasPendingCreditCharge: false,
              hasFailedCreditCharge: true,
            }
          : line,
      ),
    );
    markPaymentlineAsFailed(event.paymentlineId);
  }, [markPaymentlineAsFailed]);

  const shouldDeferOrderClosedCleanup = useCallback((orderId: number): boolean => {
    const localCreditPending = paymentlinesRef.current.some((line) => (
      line.orderId === orderId && pendingCreditChargeRef.current.has(line.id)
    ));
    const hasCompletedCash = paymentlinesRef.current.some((line) => (
      line.orderId === orderId
      && line.method === 'cash'
      && line.status === 'done'
      && !line.canceledAt
    ));
    return localCreditPending && hasCompletedCash;
  }, []);

  // Summary: Authoritative SSE credit outcome for a charge this device started: settles+closes on approval, surfaces decline/unknown, and always releases the processing lock.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleCreditTransactionResolved = useCallback((event: CreditTransactionResolvedEvent) => {
    // Release the cross-device lock on every device, regardless of which one started the charge.
    setLockedCreditPaymentlineIds((previous) => {
      if (!previous.has(event.paymentlineId)) {
        return previous;
      }
      const next = new Set(previous);
      next.delete(event.paymentlineId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === event.paymentlineId
          ? {
              ...line,
              hasPendingCreditCharge: false,
              hasFailedCreditCharge: event.paymentlineStatus !== 'done',
            }
          : line,
      ),
    );
    const timer = pendingCreditChargeRef.current.get(event.paymentlineId);
    const hasLocalPendingTimer = timer !== undefined;
    if (timer !== undefined) {
      clearTimeout(timer);
      pendingCreditChargeRef.current.delete(event.paymentlineId);
    }

    if (event.paymentlineStatus === 'done') {
      clearFailedCreditPaymentline(event.paymentlineId);
      let deferSelectionCleanup = false;
      // Full close prints the full receipt; splits print via the manual קבלה חלקית button.
      if (event.orderClosed) {
        // Only the initiating device owns automatic print dispatch; every other POS still
        // converges the order state but must not produce duplicate receipts or kitchen tickets.
        if (hasLocalPendingTimer) {
          const projected = paymentlinesRef.current.map((line) => (
            line.id === event.paymentlineId
              ? { ...line, status: 'done' as const, hasPendingCreditCharge: false, hasFailedCreditCharge: false }
              : line
          ));
          const activeProjected = projected.filter((line) => line.status === 'new' || line.status === 'done');
          const isSingleCreditClose = activeProjected.length === 1 && activeProjected[0]?.method === 'credit';
          const finalizeLocalClose = (usedDrawerId?: number) => {
            fireTakeAwayKitchenPrint(event.orderId);
            onReceiptAutoPrintRef.current?.(
              event.orderId,
              usedDrawerId == null ? undefined : { cashDrawerPrinterId: usedDrawerId },
            );
            setActiveOrders((previous) => removeById(previous, event.orderId));
            setPaymentScreenOrderId(null);
            setPaymentlines([]);
            setSelectedOrderId(null);
            setSelectedOrderTableId(null);
            setSelectedOrderTableLabel(null);
            setProductSearch('');
            setRecentResultIds([]);
            onAfterFullPaymentRef.current();
          };
          const hasCompletedCash = projected.some((line) => (
            line.orderId === event.orderId
            && line.method === 'cash'
            && line.status === 'done'
            && !line.canceledAt
          ));
          deferSelectionCleanup = hasCompletedCash;
          if (isSingleCreditClose) {
            // Single-credit full close should go straight to the post-payment receipt flow.
            if (hasCompletedCash) {
              void closeOrderWithDrawer(event.orderId, finalizeLocalClose, {
                onAlreadyClosed: () => finalizeLocalClose(),
              });
            } else {
              finalizeLocalClose();
            }
          } else {
            if (hasCompletedCash) {
              void closeOrderWithDrawer(event.orderId, finalizeLocalClose, {
                onAlreadyClosed: () => finalizeLocalClose(),
              });
            } else {
              finalizeLocalClose();
            }
          }
        }
        if (hasLocalPendingTimer && !deferSelectionCleanup) {
          setSelectedOrderTableId(null);
          setSelectedOrderTableLabel(null);
          setProductSearch('');
          setRecentResultIds([]);
        }
      }
      if (hasLocalPendingTimer) {
        notifications.show({
          color: 'green',
          title: 'התשלום אושר',
          message: 'חיוב האשראי הושלם.',
          autoClose: 3000,
        });
      }
      return;
    }
    if (event.transaction.status === 'unknown') {
      markPaymentlineAsFailed(event.paymentlineId);
      if (hasLocalPendingTimer) {
        notifications.show({
          color: 'orange',
          title: 'תשובה לא ודאית מהסליקה',
          message: 'לא התקבל אישור ודאי. בדקו את ההזמנה לפני חיוב חוזר כדי למנוע כפל חיוב.',
          autoClose: 3000,
        });
      }
      return;
    }
    markPaymentlineAsFailed(event.paymentlineId);
    if (hasLocalPendingTimer) {
      notifications.show({
        color: 'red',
        title: 'כרטיס לא עבר',
        message: 'נסה כרטיס אחר.',
        autoClose: 3000,
      });
    }
  }, [clearFailedCreditPaymentline, closeOrderWithDrawer, fireTakeAwayKitchenPrint, markPaymentlineAsFailed]);

  // Summary: Sends a split request for the selected 'new' paymentline (equal-by-count or custom-by-amount). Canonical child lines arrive via SSE paymentline.created upserts.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleSplitPaymentline = useCallback(async (
    payload: SplitPaymentlinePayload,
    paymentlineId?: number,
  ): Promise<boolean> => {
    if (!employeeSession) {
      return false;
    }
    if (paymentMutationInFlightRef.current) {
      return false;
    }
    const orderId = paymentScreenOrderId;
    if (orderId === null) {
      return false;
    }
    const newLine = typeof paymentlineId === 'number'
      ? paymentlines.find((line) => line.id === paymentlineId && line.status === 'new')
      : paymentlines.find((line) => line.status === 'new');
    if (!newLine) {
      return false;
    }
    if (failedCreditPaymentlineIds.has(newLine.id)) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לפצל',
        message: 'שורת התשלום במצב נכשל. בטלו את מצב הנכשל לפני פיצול.',
        autoClose: 3000,
      });
      return false;
    }

    paymentMutationInFlightRef.current = true;
    setIsSplittingPayment(true);
    try {
      await paymentsService.splitPaymentline(tenantId, orderId, newLine.id, payload, employeeSession);
      return true;
    } catch (splitError: unknown) {
      if (splitError instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(splitError.message),
          autoClose: 3000,
        });
      }
      return false;
    } finally {
      setIsSplittingPayment(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, failedCreditPaymentlineIds, paymentScreenOrderId, paymentlines, tenantId]);

  // Summary: Odoo partial tender — split a line by total T and pay the paid child via the tender.
  const handleSplitAndPay = useCallback(async (
    orderId: number,
    paymentlineId: number,
    payload: SplitAndPayRequest,
  ): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSplittingPayment(true);
    try {
      const fixedDrawerId = payload.tender === 'cash'
        ? await resolveValidFixedDrawerId()
        : undefined;
      await paymentsService.splitAndPay(
        tenantId,
        orderId,
        paymentlineId,
        {
          ...payload,
          ...(fixedDrawerId != null ? { cashDrawerPrinterId: fixedDrawerId } : {}),
        },
        employeeSession,
      );
      return true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSplittingPayment(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, tenantId, resolveValidFixedDrawerId]);

  // Summary: Tap-on-phone partial — split first so the device charges the child's full amount (base + tips),
  // then record the on-device witness onto that child via the shared credit spine.
  const handleSplitAndPayTapOnPhone = useCallback(async (
    orderId: number,
    paymentlineId: number,
    total: number,
  ): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSplittingPayment(true);
    let child: { paymentlineId: number; amount: number } | null = null;
    try {
      child = await paymentsService.splitForTopTender(tenantId, orderId, paymentlineId, total, employeeSession);
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
    } finally {
      setIsSplittingPayment(false);
      paymentMutationInFlightRef.current = false;
    }
    if (!child) {
      return false;
    }
    const childId = child.paymentlineId;
    // Show the child line as processing while the native TOP bridge is still waiting for card tap.
    setLockedCreditPaymentlineIds((previous) => {
      if (previous.has(childId)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(childId);
      return next;
    });
    setPaymentlines((previous) =>
      previous.map((line) =>
        line.id === childId
          ? { ...line, hasPendingCreditCharge: true }
          : line,
      ),
    );
    // Charge the device the authoritative child full amount; on cancel the child stays new (recoverable).
    try {
      const { amount: chargeAmount } = await creditPaymentsService.startTapBridge(
        tenantId,
        childId,
        employeeSession,
      );
      const witness = await runTapOnPhoneBridge(childId, chargeAmount);
      if (!witness || witness.bridgeOutcome !== 'approved') {
        setLockedCreditPaymentlineIds((previous) => {
          if (!previous.has(childId)) {
            return previous;
          }
          const next = new Set(previous);
          next.delete(childId);
          return next;
        });
        setPaymentlines((previous) =>
          previous.map((line) =>
            line.id === childId
              ? { ...line, hasPendingCreditCharge: false }
              : line,
          ),
        );
        void creditPaymentsService.releaseTapBridge(tenantId, childId, employeeSession, {
          bridgeOutcome: witness?.bridgeOutcome,
          bridgeMessage: witness?.bridgeMessage,
          rawPayload: witness?.rawPayload,
          diagnostics: witness?.diagnostics,
        });
        markPaymentlineAsFailed(childId);
        return false;
      }
      return beginCreditCharge(
        childId,
        () => creditPaymentsService.chargeTap(
          tenantId,
          childId,
          { transactionId: witness.transactionId, cardLast4: witness.cardLast4, cardBrand: witness.cardBrand },
          employeeSession ?? '',
        ),
        { skipLocalLookup: true },
      );
    } catch (error: unknown) {
      setLockedCreditPaymentlineIds((previous) => {
        const next = new Set(previous);
        next.delete(childId);
        return next;
      });
      setPaymentlines((previous) =>
        previous.map((line) =>
          line.id === childId
            ? { ...line, hasPendingCreditCharge: false }
            : line,
        ),
      );
      if (error instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
      }
      markPaymentlineAsFailed(childId);
      return false;
    }
  }, [beginCreditCharge, employeeSession, markPaymentlineAsFailed, runTapOnPhoneBridge, tenantId]);

  // Set/clear the order's global tip; SSE order.upserted patches local state.
  const handleSetOrderTip = useCallback(async (payload: TipPayload): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    if (hasOpenFailedPaymentline()) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לעדכן טיפ',
        message: 'יש שורת תשלום שנכשלה. בטלו אותה או השלימו התאמה לפני עדכון הטיפ.',
        autoClose: 3000,
      });
      return false;
    }
    const orderId = paymentScreenOrderId;
    if (orderId === null) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSettingTip(true);
    try {
      await paymentsService.setOrderTip(tenantId, orderId, payload, employeeSession);
      return true;
    } catch (tipError: unknown) {
      if (tipError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(tipError.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSettingTip(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, hasOpenFailedPaymentline, paymentScreenOrderId, tenantId]);

  // Set/clear a single payment line's own tip; SSE paymentline.created patches local state.
  const handleSetPaymentlineTip = useCallback(async (paymentlineId: number, payload: TipPayload): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    if (failedCreditPaymentlineIds.has(paymentlineId)) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לעדכן טיפ',
        message: 'שורת התשלום במצב נכשל. בטלו את מצב הנכשל לפני שינוי הטיפ.',
        autoClose: 3000,
      });
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSettingTip(true);
    try {
      await paymentsService.setPaymentlineTip(tenantId, paymentlineId, payload, employeeSession);
      return true;
    } catch (tipError: unknown) {
      if (tipError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(tipError.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSettingTip(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, failedCreditPaymentlineIds, tenantId]);

  const handleRecoverFailedCreditPaymentline = useCallback(async (orderId: number, paymentlineId: number): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    const paymentline = paymentlines.find((line) => line.id === paymentlineId && line.status === 'new');
    if (!paymentline) {
      clearFailedCreditPaymentline(paymentlineId);
      return false;
    }

    const targetAmount = resolvePaymentlineChargeAmount(orderId, paymentlineId) ?? roundMoney(paymentline.amount);

    try {
      const reconciliation = await creditPaymentsService.reconcileExternal(
        tenantId,
        {
          rangeType: 'recent',
          lookbackHours: CREDIT_RECOVERY_LOOKBACK_HOURS,
          matchWindowMinutes: 180,
        },
        employeeSession,
      );

      const candidates = reconciliation.missingInLocal.filter((row) =>
        typeof row.amount === 'number'
        && roundMoney(row.amount) === targetAmount
        && row.status === 'approved',
      );

      if (candidates.length === 0) {
        await creditPaymentsService.reconcile(tenantId, paymentlineId, employeeSession);
        clearFailedCreditPaymentline(paymentlineId);
        notifications.show({
          color: 'orange',
          title: 'לא נמצאה עסקה חיצונית תואמת',
          message: 'השורה חזרה לממתין. ניתן לנסות שוב תשלום.',
          autoClose: 3000,
        });
        return false;
      }

      const candidate = candidates
        .slice()
        .sort((left, right) => {
          const leftTs = left.occurredAt ? new Date(left.occurredAt).getTime() : 0;
          const rightTs = right.occurredAt ? new Date(right.occurredAt).getTime() : 0;
          return rightTs - leftTs;
        })[0];
      const transactionId = candidate.transactionId ?? candidate.voucherNumber;
      if (!transactionId) {
        notifications.show({
          color: 'orange',
          title: 'חסרה מזהה עסקה',
          message: 'נמצאה עסקה תואמת אך ללא מזהה שניתן לשייך אוטומטית.',
          autoClose: 3000,
        });
        return false;
      }

      const accepted = await beginCreditCharge(
        paymentlineId,
        () => creditPaymentsService.chargeTap(
          tenantId,
          paymentlineId,
          {
            transactionId,
            approvalNumber: candidate.approvalNumber ?? undefined,
            cardLast4: candidate.cardLast4 ?? undefined,
            cardBrand: candidate.cardBrand ?? undefined,
            bridgeOutcome: 'approved',
            bridgeMessage: 'Recovered from external Z-Credit reconciliation',
            rawPayload: {
              source: 'zcredit-reconcile-external',
              candidate,
              targetAmount,
            },
            diagnostics: [{
              range: reconciliation.range,
              missingInLocalCount: reconciliation.summary.missingInLocalCount,
            }],
          },
          employeeSession,
        ),
        { skipLocalLookup: true },
      );

      if (!accepted) {
        notifications.show({
          color: 'red',
          title: 'לא ניתן להשלים אוטומטית',
          message: 'השורה נשארה במצב נכשל. נסו שוב או בדקו ידנית.',
          autoClose: 3000,
        });
        return false;
      }

      notifications.show({
        color: 'green',
        title: 'נמצאה עסקה חיצונית תואמת',
        message: 'בוצעה השלמה אוטומטית של שורת התשלום.',
        autoClose: 3000,
      });
      return true;
    } catch (error: unknown) {
      if (error instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה בהתאמת Z-Credit',
          message: toHebrewApiErrorMessage(error.message),
          autoClose: 3000,
        });
      }
      return false;
    }
  }, [beginCreditCharge, clearFailedCreditPaymentline, employeeSession, paymentlines, resolvePaymentlineChargeAmount, tenantId]);

  // Void a paid line on an open order; SSE paymentline.created patches local state.
  const handleCancelPaymentline = useCallback(async (paymentlineId: number): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSettingTip(true);
    try {
      await paymentsService.cancelPaymentline(tenantId, paymentlineId, employeeSession);
      return true;
    } catch (cancelError: unknown) {
      if (cancelError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(cancelError.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSettingTip(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, tenantId]);

  // Undo a finalized cash payment: the line returns to 'new' and the order reopens; SSE patches state.
  const handleRevertCashPayment = useCallback(async (paymentlineId: number): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSettingTip(true);
    try {
      await paymentsService.revertCashPayment(tenantId, paymentlineId, employeeSession);
      return true;
    } catch (revertError: unknown) {
      if (revertError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(revertError.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSettingTip(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, tenantId]);

  // אפס: clear the order tip AND merge the open split lines back into one.
  const handleResetOrderTipAndLines = useCallback(async (): Promise<boolean> => {
    if (!employeeSession || paymentMutationInFlightRef.current) {
      return false;
    }
    if (hasOpenFailedPaymentline()) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לבצע אפס',
        message: 'יש שורת תשלום שנכשלה. בטלו אותה או השלימו התאמה לפני אפס.',
        autoClose: 3000,
      });
      return false;
    }
    const orderId = paymentScreenOrderId;
    if (orderId === null) {
      return false;
    }
    paymentMutationInFlightRef.current = true;
    setIsSettingTip(true);
    try {
      await paymentsService.resetOrderTipAndLines(tenantId, orderId, employeeSession);
      // Reset can leave a fully-discounted order with no open line: re-run the decision.
      await hydratePaymentlinesForOrder(orderId);
      return true;
    } catch (resetError: unknown) {
      if (resetError instanceof Error) {
        notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(resetError.message), autoClose: 3000 });
      }
      return false;
    } finally {
      setIsSettingTip(false);
      paymentMutationInFlightRef.current = false;
    }
  }, [employeeSession, hasOpenFailedPaymentline, hydratePaymentlinesForOrder, paymentScreenOrderId, tenantId]);

  useEffect(() => {
    activeOrdersRef.current = activeOrders;
  }, [activeOrders]);

  useEffect(() => {
    selectedOrderTableIdRef.current = selectedOrderTableId;
  }, [selectedOrderTableId]);

  // Exit-close (state א): when the selection leaves a take-away order, ask the backend to close it.
  // It closes only if the order never had an orderline; orders with items fall to the 30-min sweep.
  const previouslySelectedOrderIdRef = useRef<number | null>(null);
  useEffect(() => {
    const previous = previouslySelectedOrderIdRef.current;
    previouslySelectedOrderIdRef.current = resolvedSelectedOrderId;
    if (previous === null || previous === resolvedSelectedOrderId || !employeeSession) {
      return;
    }
    const leftOrder = activeOrdersRef.current.find((entry) => entry.id === previous);
    if (leftOrder?.orderType === 'take_away') {
      void ordersService.closeTakeAwayOnExit(tenantId, previous, employeeSession);
    }
  }, [resolvedSelectedOrderId, employeeSession, tenantId]);

  useEffect(() => {
    selectedOrderIdRef.current = resolvedSelectedOrderId;
  }, [resolvedSelectedOrderId]);

  useEffect(() => {
    isPaymentScreenOpenRef.current = isPaymentScreenOpen;
  }, [isPaymentScreenOpen]);

  // Summary: On add, snapshot the live search matches into the temp category then clear the input.
  const captureRecentResultsOnAdd = useCallback(() => {
    const search = productSearchRef.current.trim();
    if (!search) {
      return;
    }
    const matches = filterProductsBySearch(posProductsRef.current, search, posCategoriesRef.current).map((p) => p.id);
    if (matches.length === 0) {
      setProductSearch('');
      return;
    }
    setRecentResultIds(matches);
    setRecentResultsVersion((version) => version + 1);
    setProductSearch('');
  }, []);

  const clearRecentResults = useCallback(() => {
    setRecentResultIds([]);
  }, []);

  // A plain product tap adds qty 1; the backend merges into a matching pending line atomically
  // (race-free across devices). No local absolute-set merge, which lost concurrent taps (Rule 42).
  const handleAddOrderline = useCallback(async (orderId: number, productId: number) => {
    if (!employeeSession) {
      return;
    }
    captureRecentResultsOnAdd();
    setIsAddingOrderline(true);
    setError(null);
    try {
      await orderlinesService.add(tenantId, orderId, productId, 1, employeeSession);
    } catch (addError: unknown) {
      if (addError instanceof Error) {
        setError(addError.message);
      }
    } finally {
      setIsAddingOrderline(false);
    }
  }, [captureRecentResultsOnAdd, employeeSession, setError, tenantId]);

  // Summary: Adds a CONFIGURED orderline (a product with chosen variant/combo items). Never
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleAddConfiguredOrderline = useCallback(
    async (
      orderId: number,
      productId: number,
      selections: Array<{ itemId: number; selectedQuantity: number; selectionSort: number }>,
    ) => {
      if (!employeeSession) {
        return;
      }
      captureRecentResultsOnAdd();
      setIsAddingOrderline(true);
      setError(null);
      try {
        await orderlinesService.add(tenantId, orderId, productId, 1, employeeSession, selections);
      } catch (addError: unknown) {
        if (addError instanceof Error) {
          setError(addError.message);
        }
      } finally {
        setIsAddingOrderline(false);
      }
    },
    [captureRecentResultsOnAdd, employeeSession, setError, tenantId],
  );

  // Sent/skipped-line copy action: add the same product back as a new pending qty 1 line.
  const handleCopySentOrderline = useCallback(async (line: OrderlineView) => {
    if (!employeeSession || (line.status !== 'sent' && line.status !== 'skipped') || line.productId == null) {
      return;
    }
    const noteToCopy = line.note && line.note.trim().length > 0 ? line.note.trim() : null;
    const selections = (line.selections ?? [])
      .map((selection, index) => {
        if (selection.itemId == null || selection.itemId <= 0) {
          return null;
        }
        return {
          itemId: selection.itemId,
          selectedQuantity: Math.max(1, Math.trunc(selection.quantity)),
          selectionSort: index,
        };
      })
      .filter((selection): selection is { itemId: number; selectedQuantity: number; selectionSort: number } => selection !== null);

    setIsAddingOrderline(true);
    setError(null);
    try {
      await orderlinesService.add(tenantId, line.orderId, line.productId, 1, employeeSession, selections);
      if (noteToCopy) {
        const refreshed = await loadOrderlines(line.orderId);
        const newestMatchingPending = refreshed
          .filter((candidate) => candidate.status === 'pending' && candidate.productId === line.productId)
          .sort((a, b) => {
            const aTime = Number.isNaN(Date.parse(a.updatedAt)) ? 0 : Date.parse(a.updatedAt);
            const bTime = Number.isNaN(Date.parse(b.updatedAt)) ? 0 : Date.parse(b.updatedAt);
            return bTime - aTime;
          })[0];
        if (newestMatchingPending) {
          await orderlinesService.updateNote(
            tenantId,
            line.orderId,
            newestMatchingPending.id,
            noteToCopy,
            employeeSession,
          );
        }
      }
    } catch (addError: unknown) {
      if (addError instanceof Error) {
        setError(addError.message);
      }
    } finally {
      setIsAddingOrderline(false);
    }
  }, [employeeSession, loadOrderlines, setError, tenantId]);

  // Summary: Edit a still-pending line's variant/combo selections in place. The server re-prices
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleUpdateOrderlineSelections = useCallback(
    async (
      line: OrderlineView,
      selections: Array<{ itemId: number; selectedQuantity: number; selectionSort: number }>,
    ) => {
      if (!employeeSession || line.status !== 'pending') {
        return;
      }
      const live = orderlinesRef.current.find((l) => l.id === line.id);
      if (!live || live.status !== 'pending') {
        return;
      }
      setIsAdjustingOrderline(true);
      setError(null);
      try {
        await orderlinesService.updateSelections(
          tenantId,
          line.orderId,
          line.id,
          selections,
          employeeSession,
        );
      } finally {
        setIsAdjustingOrderline(false);
      }
    },
    [employeeSession, setError, tenantId],
  );

  // Summary: Set/clear a line's free-text kitchen note. Allowed on pending and sent lines so a note
  // can be attached before or after the line reaches the kitchen. The updated line (with note) arrives over the
  // orderline.updated SSE — no optimistic local patch (Rule 23/27).
  const handleUpdateOrderlineNote = useCallback(
    async (line: OrderlineView, note: string | null) => {
      if (!employeeSession) {
        return;
      }
      const live = orderlinesRef.current.find((l) => l.id === line.id);
      if (!live) {
        return;
      }
      setIsAdjustingOrderline(true);
      setError(null);
      try {
        await orderlinesService.updateNote(tenantId, line.orderId, line.id, note, employeeSession);
      } catch (noteError: unknown) {
        if (noteError instanceof Error) {
          setError(noteError.message);
        }
      } finally {
        setIsAdjustingOrderline(false);
      }
    },
    [employeeSession, setError, tenantId],
  );

  const handleSendToKitchen = useCallback(async (orderId: number, options?: {
    skipPrintJobs?: boolean;
    markSkipped?: boolean;
    actionGroupId?: string;
    selectedOrderlineIds?: number[];
    selectedOrderlineQuantities?: Array<{ orderlineId: number; quantity: number }>;
    expectedPrinterRoutes?: Array<{ printerId: number; printerName: string; orderlineIds: number[] }>;
    optimisticSentLineIds?: number[];
  }) => {
    if (!employeeSession) {
      return;
    }
    const { optimisticSentLineIds, ...serviceOptions } = options ?? {};
    setIsSendingToKitchen(true);
    setError(null);
    try {
      await orderlinesService.sendToKitchen(tenantId, orderId, employeeSession, serviceOptions);
      // Mirror the committed flip now: the orderline.sent SSE is selection-gated, so the post-send
      // deselect drops it. A late SSE re-sets the same status, so it stays idempotent (map-merge).
      const sentIdSet = optimisticSentLineIds ? new Set(optimisticSentLineIds) : null;
      setOrderlines((previous) =>
        previous.map((line) => {
          if (line.orderId !== orderId || line.status !== 'pending') {
            return line;
          }
          if (serviceOptions.markSkipped === true) {
            return { ...line, status: 'skipped' as const };
          }
          if (sentIdSet && !sentIdSet.has(line.id)) {
            return line;
          }
          return { ...line, status: 'sent' as const };
        }),
      );
    } catch (sendError: unknown) {
      if (sendError instanceof Error) {
        setError(sendError.message);
      }
    } finally {
      setIsSendingToKitchen(false);
    }
  }, [employeeSession, setError, tenantId]);

  // Summary: +1 a pending line's qty (cart stepper, atomic delta); no-op on sent/99-cap lines.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleIncrementOrderline = useCallback(async (line: OrderlineView) => {
    if (!employeeSession || line.status !== 'pending' || line.quantity >= MAX_ORDERLINE_QUANTITY) {
      return;
    }
    setIsAdjustingOrderline(true);
    setError(null);
    try {
      await orderlinesService.adjustQuantity(tenantId, line.orderId, line.id, 1, employeeSession);
    } catch (adjustError: unknown) {
      if (adjustError instanceof Error) {
        setError(adjustError.message);
      }
    } finally {
      setIsAdjustingOrderline(false);
    }
  }, [employeeSession, setError, tenantId]);

  // Summary: -1 a pending line's qty (cart stepper); qty 1 → soft-remove, else atomic delta -1.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  const handleDecrementOrderline = useCallback(async (line: OrderlineView) => {
    if (!employeeSession || line.status !== 'pending') {
      return;
    }
    setIsAdjustingOrderline(true);
    setError(null);
    try {
      if (line.quantity <= 1) {
        await orderlinesService.updateQuantity(tenantId, line.orderId, line.id, 0, employeeSession);
      } else {
        await orderlinesService.adjustQuantity(tenantId, line.orderId, line.id, -1, employeeSession);
      }
    } catch (adjustError: unknown) {
      if (adjustError instanceof Error) {
        setError(adjustError.message);
      }
    } finally {
      setIsAdjustingOrderline(false);
    }
  }, [employeeSession, setError, tenantId]);

  const handleSetOrderlineQuantity = useCallback(async (line: OrderlineView, quantity: number) => {
    if (!employeeSession || line.status !== 'pending') {
      return;
    }

    const normalizedQuantity = Math.min(
      MAX_ORDERLINE_QUANTITY,
      Math.max(0, Math.trunc(quantity)),
    );

    if (normalizedQuantity === line.quantity) {
      return;
    }

    setIsAdjustingOrderline(true);
    setError(null);
    try {
      await orderlinesService.updateQuantity(
        tenantId,
        line.orderId,
        line.id,
        normalizedQuantity,
        employeeSession,
      );
    } catch (adjustError: unknown) {
      if (adjustError instanceof Error) {
        setError(adjustError.message);
      }
    } finally {
      setIsAdjustingOrderline(false);
    }
  }, [employeeSession, setError, tenantId]);

  // Summary: Opens transfer flow in item-select mode for the chosen source table/order.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-transfer
  const handleBeginTransfer = useCallback((sourceOrderId: number, sourceTableId: number | null, sourceTableLabel: string, sourceTableIds?: number[]) => {
    setTransferMode({
      step: 'item-select',
      sourceOrderId,
      sourceTableId,
      sourceTableIds: sourceTableIds ?? (sourceTableId === null ? [] : [sourceTableId]),
      sourceTableLabel,
    });
  }, []);

  // Summary: Advances transfer flow from item-select to pick-table with chosen orderline ids.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-transfer
  const handleTransferItemsSelected = useCallback((selectedLines: TransferOrderlineMove[]) => {
    if (transferMode.step !== 'item-select') {
      return;
    }
    const { sourceOrderId, sourceTableId, sourceTableIds, sourceTableLabel } = transferMode;
    setTransferMode({ step: 'pick-table', sourceOrderId, sourceTableId, sourceTableIds, sourceTableLabel, selectedLines });
  }, [transferMode]);

  // Summary: Cancels transfer flow from any step and resets state back to idle.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-transfer
  const handleCancelTransfer = useCallback(() => {
    setTransferMode({ step: 'idle' });
  }, []);

  // Summary: Enters merge pick-tables mode with the long-pressed table as the central table.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-merge
  const handleBeginMerge = useCallback((centralTableId: number, centralTableLabel: string, centralOrderId: number | null) => {
    setMergeMode({
      step: 'pick-tables',
      centralTableId,
      centralTableLabel,
      centralOrderId,
      selectedTableIds: [centralTableId],
    });
  }, []);

  const handleToggleMergeTable = useCallback((tableId: number, _tableLabel: string) => {
    setMergeMode((prev) => {
      if (prev.step !== 'pick-tables') return prev;
      if (tableId === prev.centralTableId) return prev;
      const already = prev.selectedTableIds.includes(tableId);
      const selectedTableIds = already
        ? prev.selectedTableIds.filter((id) => id !== tableId)
        : [...prev.selectedTableIds, tableId];
      return { ...prev, selectedTableIds };
    });
  }, []);

  // Summary: Cancels merge flow from any step and resets merge state to idle.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-merge
  const handleCancelMerge = useCallback(() => {
    setMergeMode({ step: 'idle' });
  }, []);

  // Summary: Opens the split modal for the merge group that owns the given table.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-merge
  const handleBeginSplitMerge = useCallback((tableId: number) => {
    const link = activeMergeLinks.find((l) => l.tablePk === tableId);
    if (!link) return;
    setSplitMergeOrderPk(link.orderPk);
  }, [activeMergeLinks]);

  const handleCancelSplitMerge = useCallback(() => {
    setSplitMergeOrderPk(null);
  }, []);

  // Summary: Deactivates the selected merge link IDs and refreshes floor state.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-merge
  const handleConfirmSplitMerge = useCallback(async (linkIds: number[]) => {
    if (!employeeSession || splitMutationInFlightRef.current || splitMergeOrderPk === null) return;
    splitMutationInFlightRef.current = true;
    setIsSplittingMerge(true);
    try {
      await mergeService.deactivateMergeLinks(tenantId, linkIds, splitMergeOrderPk, employeeSession);
      await Promise.all([loadMergeLinks(), loadActiveOrders()]);
      setSplitMergeOrderPk(null);
      notifications.show({
        color: 'green',
        message: linkIds.length === 1 ? 'שולחן 1 פוצל מהמיזוג' : `${linkIds.length} שולחנות פוצלו מהמיזוג`,
        autoClose: 3000,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה בפיצול',
          message: toHebrewApiErrorMessage(err.message),
          autoClose: 3000,
        });
      }
    } finally {
      splitMutationInFlightRef.current = false;
      setIsSplittingMerge(false);
    }
  }, [employeeSession, tenantId, splitMergeOrderPk, loadMergeLinks, loadActiveOrders]);

  const resolveOpenOrderByTable = useCallback(async (tableId: number): Promise<OrderView | null> => {
    if (!employeeSession) {
      return null;
    }
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const refreshed = await ordersService.listActiveOpen(tenantId, employeeSession);
      setActiveOrders(refreshed);
      const found = refreshed.find((order) => order.tableId === tableId) ?? null;
      if (found) {
        return found;
      }
      // Newly opened target orders can arrive a moment later on slower devices/networks.
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return null;
  }, [employeeSession, tenantId, setActiveOrders]);

  const refreshOrderlinesForCurrentOrder = useCallback(async () => {
    if (!employeeSession || selectedOrderId === null) return;
    try {
      loadedOrderlinesOrderIdRef.current = null;
      await loadOrderlines(selectedOrderId);
    } catch {
      // non-critical — SSE refresh, ignore errors
    }
  }, [employeeSession, selectedOrderId, loadOrderlines, loadedOrderlinesOrderIdRef]);

  const refreshPaymentlinesForCurrentOrder = useCallback(async () => {
    if (!employeeSession || selectedOrderId === null) return;
    try {
      const lines = await paymentsService.listForOrder(tenantId, selectedOrderId, employeeSession);
      if (lines.length > 0) setPaymentlines((prev) => {
        // Merge server lines into current state — don't overwrite lines that were added
        // locally (e.g. a freshly created 'new' line) and aren't on the server yet.
        const merged = [...prev];
        for (const line of lines) {
          const idx = merged.findIndex((p) => p.id === line.id);
          if (idx >= 0) merged[idx] = line;
          else merged.push(line);
        }
        return merged;
      });
    } catch {
      // non-critical — SSE refresh, ignore errors
    }
  }, [employeeSession, selectedOrderId, tenantId]);

  const handleConfirmMerge = useCallback(async () => {
    if (mergeMode.step !== 'pick-tables' || !employeeSession || transferMutationInFlightRef.current) return;

    const { centralTableId, centralOrderId, selectedTableIds } = mergeMode;
    const otherTableIds = selectedTableIds.filter((id) => id !== centralTableId);
    if (otherTableIds.length === 0) return;

    const currentOrders = activeOrdersRef.current;

    // If any involved order has a persistent (percentage) discount or tip, prompt user to clear first
    const allInvolvedOrders = selectedTableIds
      .map((id) => currentOrders.find((o) => o.tableId === id))
      .filter(Boolean);
    const hasPercentDiscount = allInvolvedOrders.some((o) => o!.discountMode === 'percentage');
    const hasPercentTip = allInvolvedOrders.some((o) => o!.tipInputMode === 'percent');
    if (hasPercentDiscount || hasPercentTip) {
      setMergeConflict({ hasPercentDiscount, hasPercentTip });
      return;
    }

    // If any selected table already belongs to an active merge group, that group's order
    // is the true central — joining a group always absorbs into the existing order, never creates a parallel one.
    const existingGroupOrderPk = activeMergeLinks.find((l) => selectedTableIds.includes(l.tablePk))?.orderPk ?? null;

    // mergeMode.centralOrderId may be stale (null) if the order was just opened via the
    // diners numpad flow — fall back to a live lookup from activeOrdersRef.
    let resolvedCentralOrderId = existingGroupOrderPk
      ?? centralOrderId
      ?? (currentOrders.find((o) => o.tableId === centralTableId)?.id ?? null);

    if (resolvedCentralOrderId === null) {
      const firstFilledTableId = otherTableIds.find((id) => currentOrders.some((o) => o.tableId === id));
      if (firstFilledTableId !== undefined) {
        // Central is empty but another selected table has an order — promote it below inside try.
        // resolvedCentralOrderId stays null here; handled inside try block.
      } else {
        // All tables are empty — open the central table first via the diners numpad, then re-run.
        // Early return before the try/finally so mergeMode is NOT reset to idle.
        onNeedDinersForMerge(centralTableId, mergeMode.centralTableLabel);
        return;
      }
    }

    transferMutationInFlightRef.current = true;
    setIsTransferring(true);
    try {
      if (resolvedCentralOrderId === null) {
        // Central table is empty but another selected table has an order — promote it.
        const firstFilledTableId = otherTableIds.find((id) => currentOrders.some((o) => o.tableId === id))!;
        const firstOrder = currentOrders.find((o) => o.tableId === firstFilledTableId)!;
        await transferService.transferOrderToTable(tenantId, firstOrder.id, centralTableId, employeeSession);
        resolvedCentralOrderId = firstOrder.id;
      }

      // Collect totals from source orders BEFORE merging — after mergeOrderIntoOrder the
      // source paymentlines are moved to the central order and can no longer be fetched by source id.
      const involvedOrders = selectedTableIds
        .map((id) => currentOrders.find((o) => o.tableId === id))
        .filter(Boolean) as OrderView[];
      const orderLevelTip = involvedOrders.reduce((sum, o) => {
        return o.tipInputMode === 'value' ? sum + (o.tipInputValue ?? 0) : sum;
      }, 0);
      // Fetch done paymentlines from source orders now (pre-merge) to capture per-line tips.
      // The summary only sees order.tipInputValue (paymentlines=[]) so we must fold line tips in.
      const sourceOrderIds = involvedOrders
        .filter((o) => o.id !== resolvedCentralOrderId)
        .map((o) => o.id);
      const sourcePaymentlines = (await Promise.all(
        sourceOrderIds.map((id) => paymentsService.listForOrder(tenantId, id, employeeSession).catch(() => [])),
      )).flat();
      const paymentLineTip = sourcePaymentlines
        .filter((pl) => pl.status === 'done' && !pl.canceledAt)
        .reduce((sum, pl) => {
          if (pl.tipInputMode === 'value') return sum + (pl.tipInputValue ?? 0);
          if (pl.tipInputMode === 'percent') return sum + Math.round((pl.amount * (pl.tipInputValue ?? 0) / 100) * 10) / 10;
          return sum;
        }, 0);
      const totalTip = orderLevelTip + paymentLineTip;

      // Merge all selected tables' orders into the central order.
      // Use ALL selectedTableIds (not just otherTableIds) so that when the user long-pressed
      // a non-central table to start the merge, their order is also absorbed.
      let orderlinesWereMerged = false;
      for (const tableId of selectedTableIds) {
        const sourceOrder = currentOrders.find((o) => o.tableId === tableId);
        if (!sourceOrder) continue; // empty or already-closed table — nothing to merge
        if (sourceOrder.id === resolvedCentralOrderId) continue; // this IS the central order
        await mergeService.mergeOrderIntoOrder(tenantId, sourceOrder.id, resolvedCentralOrderId, employeeSession);
        orderlinesWereMerged = true;
      }
      if (totalTip > 0) {
        await paymentsService.setOrderTip(tenantId, resolvedCentralOrderId, { tipInputMode: 'value', tipInputValue: totalTip }, employeeSession);
      }

      const alreadyLinkedTablePks = new Set(
        activeMergeLinks
          .filter((l) => l.orderPk === resolvedCentralOrderId)
          .map((l) => l.tablePk),
      );
      const newMergeLinks = selectedTableIds
        .filter((tableId) => !alreadyLinkedTablePks.has(tableId))
        .map((tableId) => ({ tablePk: tableId, orderPk: resolvedCentralOrderId as number }));
      if (newMergeLinks.length > 0) {
        await mergeService.createMergeLinks(tenantId, newMergeLinks, employeeSession);
      }

      await Promise.all([loadActiveOrders(), loadMergeLinks()]);
      // Force reload orderlines for the central order — the order ID didn't change so the
      // normal effect won't trigger, but new orderlines were just merged in.
      loadedOrderlinesOrderIdRef.current = null;
      await loadOrderlines(resolvedCentralOrderId as number);
      if (orderlinesWereMerged) {
        await hydratePaymentlinesForOrder(resolvedCentralOrderId as number);
      }
      notifications.show({
        color: 'green',
        message: 'מיזוג בוצע בהצלחה',
        autoClose: 3000,
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה במיזוג',
          message: toHebrewApiErrorMessage(err.message),
          autoClose: 3000,
        });
      }
    } finally {
      transferMutationInFlightRef.current = false;
      setIsTransferring(false);
      setMergeMode({ step: 'idle' });
    }
  }, [mergeMode, employeeSession, tenantId, loadActiveOrders, loadMergeLinks, onNeedDinersForMerge, activeMergeLinks, hydratePaymentlinesForOrder, loadOrderlines, loadedOrderlinesOrderIdRef]);

  const handleMergeConflictCancel = useCallback(() => {
    setMergeConflict(null);
  }, []);

  const handleMergeConflictConfirm = useCallback(async () => {
    if (!mergeConflict || mergeMode.step !== 'pick-tables' || !employeeSession || transferMutationInFlightRef.current) return;
    const { selectedTableIds } = mergeMode;
    const currentOrders = activeOrdersRef.current;
    const involvedOrders = selectedTableIds
      .map((id) => currentOrders.find((o) => o.tableId === id))
      .filter(Boolean) as OrderView[];

    transferMutationInFlightRef.current = true;
    setIsTransferring(true);
    try {
      // Clear percentage discount from all affected orders
      if (mergeConflict.hasPercentDiscount) {
        const ordersWithPercentDiscount = involvedOrders.filter((o) => o.discountMode === 'percentage');
        await Promise.all(
          ordersWithPercentDiscount.map((o) =>
            ordersService.updateGlobalDiscount(tenantId, o.id, null, null, employeeSession),
          ),
        );
      }
      // Clear percentage tip from all affected orders — use setOrderTip(null) instead of
      // resetOrderTipAndLines so we don't recreate a new paymentline. The merge will cancel
      // open paymentlines itself; creating one here then having the merge cancel it confuses
      // the source-order close guard (billable ≠ paid).
      if (mergeConflict.hasPercentTip) {
        const ordersWithPercentTip = involvedOrders.filter((o) => o.tipInputMode === 'percent');
        await Promise.all(
          ordersWithPercentTip.map((o) =>
            paymentsService.setOrderTip(tenantId, o.id, { tipInputMode: null, tipInputValue: null }, employeeSession),
          ),
        );
      }
    } catch {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: 'לא ניתן היה לנקות את ההנחה/טיפ לפני המיזוג',
        autoClose: 3000,
      });
      setMergeConflict(null);
      transferMutationInFlightRef.current = false;
      setIsTransferring(false);
      return;
    }

    setMergeConflict(null);
    // Reload orders and sync the ref immediately so handleConfirmMerge sees the cleared discount/tip.
    // Release the lock first — handleConfirmMerge acquires it again internally.
    transferMutationInFlightRef.current = false;
    setIsTransferring(false);
    const freshOrders = await loadActiveOrders();
    activeOrdersRef.current = freshOrders;
    void handleConfirmMerge();
  }, [mergeConflict, mergeMode, employeeSession, tenantId, handleConfirmMerge, activeOrdersRef, loadActiveOrders]);

  // Summary: Executes transfer after target selection, branching for same-table, open-target, and empty-target paths.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain-transfer
  const handleExecuteTransferFull = useCallback(async (
    targetTableId: number | null,
    targetOrderId: number | null,
    isFullTransfer: boolean,
    targetLabel = targetTableId === null ? 'הזמנה' : `שולחן ${targetTableId}`,
  ) => {
    if (transferMode.step !== 'pick-table' || !employeeSession || transferMutationInFlightRef.current) {
      return false;
    }
    const { sourceOrderId, sourceTableIds, selectedLines } = transferMode;

    if (targetOrderId === sourceOrderId || (targetTableId !== null && sourceTableIds.includes(targetTableId))) {
      setTransferMode({ step: 'idle' });
      return false;
    }

    transferMutationInFlightRef.current = true;
    setIsTransferring(true);
    try {
      if (isFullTransfer && targetOrderId === null && targetTableId !== null) {
        await transferService.transferOrderToTable(tenantId, sourceOrderId, targetTableId, employeeSession);
      } else if (targetOrderId !== null) {
        await transferService.transferOrderlines(tenantId, sourceOrderId, targetOrderId, selectedLines, employeeSession);
      } else {
        if (targetTableId === null) {
          throw new Error('TARGET_ORDER_NOT_FOUND');
        }
        // Partial to empty table: open order first, then transfer lines
        await ordersService.openForTable(tenantId, targetTableId, employeeSession, stationaryName);
        const newOrder = await resolveOpenOrderByTable(targetTableId);
        if (!newOrder) {
          throw new Error('Could not resolve new order for target table');
        }
        await transferService.transferOrderlines(tenantId, sourceOrderId, newOrder.id, selectedLines, employeeSession);
      }
      const movedUnits = selectedLines.reduce((sum, line) => sum + line.quantity, 0);
      notifications.show({
        color: 'green',
        title: 'הועבר בהצלחה',
        message: `${movedUnits} יחידות הועברו ל${targetLabel}`,
        autoClose: 3000,
      });
      return true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        notifications.show({
          color: 'red',
          title: 'שגיאה',
          message: toHebrewApiErrorMessage(err.message),
          autoClose: 3000,
        });
      }
      return false;
    } finally {
      transferMutationInFlightRef.current = false;
      setIsTransferring(false);
      setTransferMode({ step: 'idle' });
      loadedOrderlinesOrderIdRef.current = null;
      void loadMergeLinks();
    }
  }, [transferMode, employeeSession, tenantId, resolveOpenOrderByTable, loadMergeLinks, stationaryName]);

  // Summary: Clear table-scoped cart state when table selection/session is cleared.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  useEffect(() => {
    if (resolvedSelectedOrderId === null || !employeeSession) {
      setOrderlines([]);
      setPaymentlines([]);
      setPaymentScreenOrderId(null);
      setIsReceiptScreenOpen(false);
      loadedOrderlinesOrderIdRef.current = null;
    }
  }, [resolvedSelectedOrderId, employeeSession]);


  // Summary: Load POS products once per employee session.
  // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-use-pos-orders-domain
  useEffect(() => {
    if (!employeeSession) {
      return;
    }
    if (posProductsLoadedForSessionRef.current === employeeSession) {
      return;
    }
    posProductsLoadedForSessionRef.current = employeeSession;
    void loadPosProducts();
  }, [employeeSession, loadPosProducts]);

  // Architectural summary: Load POS categories once per employee session.
  useEffect(() => {
    if (!employeeSession) {
      return;
    }
    if (posCategoriesLoadedForSessionRef.current === employeeSession) {
      return;
    }
    posCategoriesLoadedForSessionRef.current = employeeSession;
    void loadPosCategories();
  }, [employeeSession, loadPosCategories]);

  useEffect(() => {
    if (resolvedSelectedOrderId === null || !employeeSession) {
      return;
    }
    if (!selectedTableOrder) {
      setOrderlines([]);
      loadedOrderlinesOrderIdRef.current = null;
      setOrderlinesLoadedOrderId(null);
      return;
    }
    if (loadedOrderlinesOrderIdRef.current === selectedTableOrder.id) {
      return;
    }
    loadedOrderlinesOrderIdRef.current = selectedTableOrder.id;
    // Flash guard: skip fetch only for a never-lined order; history forces a restored load.
    if (
      selectedTableOrder.totalAmount === 0
      && selectedTableOrder.lastActivityAt === selectedTableOrder.createdAt
      && !selectedTableOrder.hasOrderlineHistory
    ) {
      setOrderlines([]);
      setOrderlinesLoadedOrderId(selectedTableOrder.id);
      return;
    }
    void loadOrderlines(selectedTableOrder.id);
  }, [resolvedSelectedOrderId, employeeSession, selectedTableOrder, loadOrderlines]);

  const handleOpenReceiptScreen = useCallback((orderId: number) => {
    setIsReceiptScreenOpen(true);
    setIsPaymentScreenOpen(false);
    // Drop payment-screen prompts carried over; keep the bill viewable (zero-total + overage).
    setPaymentZeroTotalPrompt(null);
    setPaymentOveragePrompt(null);
    void hydratePaymentlinesForOrder(orderId, { forReceiptScreen: true });
  }, [hydratePaymentlinesForOrder]);

  const handleSaveGuestName = useCallback(
    async (orderId: number, guestName: string | null) => {
      if (!employeeSession || saveGuestNameInFlightRef.current) return false;
      saveGuestNameInFlightRef.current = true;
      setIsSavingGuestName(true);
      try {
        await ordersService.updateGuestName(tenantId, orderId, guestName, employeeSession);
        return true;
      } catch (saveError: unknown) {
        if (saveError instanceof Error) {
          notifications.show({
            color: 'red',
            title: 'שגיאה',
            message: toHebrewApiErrorMessage(saveError.message),
            autoClose: 3000,
          });
        }
        return false;
      } finally {
        saveGuestNameInFlightRef.current = false;
        setIsSavingGuestName(false);
      }
    },
    [employeeSession, tenantId],
  );

  const clearOrderSelection = useCallback(() => {
    setPaymentScreenOrderId(null);
    setPaymentOveragePrompt(null);
    setPaymentZeroTotalPrompt(null);
    setPaymentlines([]);
    setSelectedOrderTableId(null);
    setSelectedOrderTableLabel(null);
    setSelectedOrderId(null);
    setProductSearch('');
    setRecentResultIds([]);
    loadedOrderlinesOrderIdRef.current = null;
    setOrderlinesLoadedOrderId(null);
  }, []);

  const resetForEmployeeSwitch = useCallback(() => {
    setActiveOrders([]);
    setSelectedOrderTableId(null);
    setSelectedOrderTableLabel(null);
    setSelectedOrderId(null);
    setPaymentScreenOrderId(null);
    setPaymentOveragePrompt(null);
    setPaymentZeroTotalPrompt(null);
    setIsReceiptScreenOpen(false);
    setPaymentlines([]);
    setOrderlines([]);
    setPosProducts([]);
    setPosCategories([]);
    setProductSearch('');
    setRecentResultIds([]);
    initialOrdersLoadedForSessionRef.current = null;
    posProductsLoadedForSessionRef.current = null;
    posCategoriesLoadedForSessionRef.current = null;
    loadedOrderlinesOrderIdRef.current = null;
    setOrderlinesLoadedOrderId(null);
  }, []);

  return {
    activeOrders,
    isLoadingOrders,
    isOpeningOrder,
    isUpdatingDiners,
    isClosingOrder,
    selectedOrderTableId,
    selectedOrderTableLabel,
    selectedOrderId: resolvedSelectedOrderId,
    selectedOrder: selectedTableOrder,
    isPaymentScreenOpen,
    isReceiptScreenOpen,
    isSavingGuestName,
    paymentlines,
    posTerminals,
    setPosTerminals,
    isLoadingPaymentlines,
    isPreparingPaymentline,
    isMarkingPaymentDone,
    isSplittingPayment,
    isSettingTip,
    paymentOveragePrompt,
    paymentZeroTotalPrompt,
    isResolvingPaymentOverage,
    isResolvingZeroTotalClose,
    failedCreditPaymentlineIds,
    orderlines,
    isLoadingOrderlines,
    orderlinesLoadedOrderId,
    isAddingOrderline,
    isAdjustingOrderline,
    isSendingToKitchen,
    posProducts,
    isLoadingPosProducts,
    posCategories,
    setPosCategories,
    productSearch,
    setProductSearch,
    recentResultIds,
    recentResultsVersion,
    clearRecentResults,
    setSelectedOrderTableId,
    setSelectedOrderTableLabel,
    setSelectedOrderId,
    handleOpenTakeAway,
    setIsPaymentScreenOpen,
    setIsReceiptScreenOpen,
    setPaymentlines,
    setOrderlines,
    setActiveOrders,
    setPosProducts,
    loadActiveOrders,
    loadOrderlines,
    handleUpdateDiners,
    handleToggleOrderDeposit,
    handleToggleOrderVip,
    handleOpenOrderWithDiners,
    handleCloseOrder,
    handleOpenPaymentScreen,
    applyQuickChargeAmount,
    cancelQuickCharge,
    quickChargeAmountOrderId,
    clearQuickChargeAmountPrompt,
    handleOpenReceiptScreen,
    handleSaveGuestName,
    handlePayAndCloseIfDone,
    handleSettleCashOverage,
    dismissPaymentOverageToMenu,
    confirmPaymentOverageClose,
    cashDrawerPrompt,
    confirmCashDrawerSelection,
    dismissCashDrawerPrompt,
    dismissZeroTotalToMenu,
    confirmZeroTotalClose,
    handleChargeCredit,
    handleChargeManualCredit,
    handleChargeTapOnPhone,
    handleCreditTransactionResolved,
    shouldDeferOrderClosedCleanup,
    handleCreditTransactionPending,
    handleTapOnPhonePending,
    handleTapOnPhoneReleased,
    lockedCreditPaymentlineIds,
    clearFailedCreditPaymentline,
    handleSplitPaymentline,
    handleSplitAndPay,
    handleSplitAndPayTapOnPhone,
    handleSetOrderTip,
    handleSetPaymentlineTip,
    handleRecoverFailedCreditPaymentline,
    handleCancelPaymentline,
    handleRevertCashPayment,
    handleResetOrderTipAndLines,
    handleAddOrderline,
    handleAddConfiguredOrderline,
    handleCopySentOrderline,
    handleIncrementOrderline,
    handleDecrementOrderline,
    handleUpdateOrderlineSelections,
    handleUpdateOrderlineNote,
    handleSetOrderlineQuantity,
    handleSendToKitchen,
    clearOrderSelection,
    resetForEmployeeSwitch,
    loadMergeLinks,
    transferMode,
    isTransferring,
    setIsTransferring,
    handleBeginTransfer,
    handleTransferItemsSelected,
    handleCancelTransfer,
    handleExecuteTransferFull,
    mergeMode,
    activeMergeLinks,
    handleBeginMerge,
    handleCancelMerge,
    handleToggleMergeTable,
    handleConfirmMerge,
    splitMergeOrderPk,
    isSplittingMerge,
    handleBeginSplitMerge,
    handleCancelSplitMerge,
    handleConfirmSplitMerge,
    mergeConflict,
    handleMergeConflictCancel,
    handleMergeConflictConfirm,
    activeOrdersRef,
    selectedOrderTableIdRef,
    selectedOrderIdRef,
    loadedOrderlinesOrderIdRef,
    isPaymentScreenOpenRef,
    initialOrdersLoadedForSessionRef,
    transferMutationInFlightRef,
    refreshPaymentlinesForCurrentOrder,
    refreshOrderlinesForCurrentOrder,
    orderlinesRef,
    getBillableTotalForOrder,
  };
}
